import "maplibre-gl/dist/maplibre-gl.css";
import "./style.css";
import { initCrashReporter, reportError } from "./crash-reporter.ts";
import { parseGpx, simplifyToMax } from "../shared/editor-geom.ts";
import { readGraphicsAcceleration, type GraphicsAccelReport } from "./graphics-accel.ts";
import { normalizeLineStyle, normalizePointStyle, parseStyleDefaults, type LinePattern } from "../shared/feature-style.ts";
import { readStyleDefaults, writeStyleDefaults } from "./style-defaults.ts";
import {
  applyPanelTheme,
  isPanelTheme,
  rangeFraction,
  readPanelTheme,
  writePanelTheme,
  type PanelTheme,
} from "./panel-theme.ts";
import { graphFromQuestRoute, type RouteResult } from "../shared/route-graph.ts";
import {
  CUSTOM_SLOT_COUNT,
  DEFAULT_FILL_COLOR,
  FILL_PALETTE_IDS,
  FILL_SWATCHES,
  NO_FILL,
  PALETTE_COLUMNS,
  sharedPaletteSwatches,
  isFillPaletteId,
  normalizeColor,
  paletteCells,
  paletteSelectionIndex,
  parseCustomSlots,
  type FillPaletteId,
  type PaletteCell,
} from "../shared/region-fill.ts";
import {
  applyStaticCopy,
  bootLocale,
  formatDistance,
  isMessageKey,
  keyForEnglish,
  locale,
  setLocaleSwitchHandler,
  setMessageOverrides,
  storeLocale,
  t,
  urlWithoutLang,
  type Locale,
} from "./i18n/index.ts";
import { appendLanguageSwitch, fillLanguageMenu } from "./i18n/switcher.ts";
import { mountLegalFooter, refreshLegalFooterLinks } from "./legal-footer.ts";
import type { MessageKey } from "./i18n/en.ts";
import type { AuthUser, PieceGraph } from "../shared/types.ts";
import type { UserPieceData } from "../shared/user-piece.ts";
import {
  ApiError,
  bindQuestRoute,
  deleteMission,
  deleteUserPiece as deleteUserPieceRemote,
  getConfig,
  getMe,
  getMission,
  cachedQuest,
  clearQuestCache,
  getQuest,
  rememberPublicQuest,
  getUserPiece,
  listMyMissions,
  listNearbyQuests,
  listSavedQuests,
  questGpxUrl,
  saveQuest,
  unsaveQuest,
  saveUserPiece,
  signInWithGoogle,
  signOut,
} from "./api.ts";
import { initAdsFromPublicConfig, readAdPreviewEnabled, syncAdPreviewFromLocation } from "./ads.ts";
import { questSettingsSummary, walkLabel } from "./quest/summary.ts";
import {
  detailOpenable,
  homeListed,
  myQuestsListed,
  isSpotOnlyListQuest,
  questListRouteCoordinates,
  questMapPoint,
  usesQuestPageTemplate,
} from "../shared/quest-feed.ts";
import { rememberHomeSpotTitleWidth } from "../shared/home-quest-map-fit.ts";
import type { NearbyQuest, QuestDetail } from "./quest/types.ts";
import type { HomeSpotPin } from "./home/home-quest-spots.ts";
import { whenMapHasStyle } from "../shared/map-style-safe.ts";
import { clearDraft, loadDraft, saveDraft } from "./draft-store.ts";
import {
  actionAllowedInSession,
  createEditorSession,
  editingActive,
  enterEdit,
  markSessionDirty,
  markSessionSaved,
  openPieceSession,
  requestLeaveEdit,
  resolveLeaveEdit,
  type EditorSession,
  type LeaveChoice,
} from "./editor-mode.ts";
import { ACTION_IDS, ACTION_LABEL, isActionId, type ActionId } from "./input/actions.ts";
import { basemapToggleFromKey } from "./basemap-keyboard.ts";
import { streetViewOpenFromKey } from "./street-view/street-view-keyboard.ts";
import { focusOwnsKey } from "./input/typing.ts";
import {
  belongsToSignedInUser,
  ownPieceDeletable,
  ownPieceEditable,
  templateDeletable,
  type ActivePieceAccess,
} from "./piece-access.ts";
import type { DeleteMenuChoice } from "../shared/delete-menu.ts";
import {
  acceleratorIndex,
  acceleratorLetter,
  actionMatchesKey,
  actionMatchesPointer,
  bindingFromKey,
  bindingFromPointer,
  bindingFromWheel,
  bindingTokens,
  detectPlatform,
  findConflict,
  loadKeymap,
  matchKey,
  replaceBinding,
  resetKeymap,
  saveKeymap,
  undoHint,
  type Binding,
  type KeyState,
  type Platform,
  type PointerState,
  type StoredKeymap,
} from "./input/keymap.ts";
import { setMaptilerKey, setVworldKey } from "./basemap.ts";
import { normalizeStreetViewKeys } from "../shared/street-view-keys.ts";
import { applyStreetViewKeysToMaps } from "./street-view/register.ts";
import { setStreetViewKeys } from "./street-view/street-view-state.ts";
import { MapEditor, MIN_EDIT_ZOOM, type RouteIssueKind } from "./map-editor.ts";
import { splitOverlayInsets, type ScreenRect } from "../shared/rotated-fit.ts";
import { compassStart } from "../shared/quest-compass.ts";
import { routeCanvasBearing } from "../shared/route-canvas.ts";
import { bindPanelMenu } from "./panel-menu.ts";
import { pieceStore } from "./piece-store.ts";
import { SnapshotOverlay } from "./snapshot-overlay.ts";
import { QuestPage } from "./quest/quest-page.ts";
import { mountPicker } from "./quest/picker.ts";
import { RouteTraceEditor } from "./quest/route-trace-editor.ts";
import { questSaveHeartEnabled } from "./quest/save-heart.ts";
import {
  bindHomeTabs,
  bindQuestListClicks,
  questTitle,
  renderMyQuestList,
  renderNearbyList,
  renderSavedList,
  syncListCurrent,
  type QuestListClick,
} from "./home/home-list.ts";
import { ListPreviewPanel } from "./home/list-preview.ts";
import {
  nearbySearchAreaStaleForMap,
  type NearbyListAnchor,
} from "./home/nearby-list-anchor.ts";
import { createNearbyViewer } from "./home/nearby-viewer.ts";
import {
  clearHomeBrowseReturn,
  readHomeBrowseReturn,
  writeHomeBrowseReturn,
  type HomeBrowseReturn,
} from "./home/home-browse-persist.ts";
import {
  formatHomeBrowseUrl,
  parseHomeBrowseSearch,
  stripHomeBrowseViewFromUrl,
} from "./home/home-browse-url.ts";
import { escapeHtml } from "./html.ts";
import {
  announceRouteSaved,
  clearQuestRoutePieceId,
  onAnyRouteSaved,
  readQuestRoutePieceId,
  writeQuestRoutePieceId,
} from "./quest/route-sync.ts";
import {
  canonicalQuestUrl,
  matchQuestRoute,
  questEditPath,
  questRoutePath,
  publishedQuestViewHref,
  questViewPath,
} from "./quest/router.ts";
import {
  mentionsPiece,
  questRouteAdminLive,
  ROUTE_WINDOW_MESSAGES,
  routeAccess,
} from "./quest/route-window.ts";
import { confirmAdminPublishedSave } from "./quest/editor-banner-collapse.ts";
import { paintQuestRouteBanner } from "./quest/quest-route-banner.ts";
import { bindMapOverlayWheelIsolation } from "./map-overlay-wheel.ts";

initCrashReporter();
let panelTheme: PanelTheme = readPanelTheme(safeLocalStorage());
applyPanelTheme(document.documentElement, panelTheme);
bootLocale();
const canonicalEntry = canonicalQuestUrl(location.pathname, location.search);
if (canonicalEntry) history.replaceState(null, "", canonicalEntry);
const entryRoute = matchQuestRoute(location.pathname);
if (entryRoute?.kind === "view") document.getElementById("quest-ssr")?.remove();
/** Set when this tab is the map editor bound to a quest draft's route (`/quest/:id/route`). */
const questRouteId = entryRoute?.kind === "route" ? entryRoute.id : null;
if (questRouteId) {
  setMessageOverrides(ROUTE_WINDOW_MESSAGES);
  document.body.classList.add("route-window");
}
applyStaticCopy();
if (questRouteId) document.title = t("qroute.docTitleUntitled");
{
  const gsi = document.querySelector<HTMLScriptElement>("script[src*='accounts.google.com/gsi/client']");
  if (gsi && !gsi.src.includes(`hl=${locale()}`)) gsi.src = `https://accounts.google.com/gsi/client?hl=${locale()}`;
}

const STYLE_RELOAD_KEY = "mapus.styleReload";

/** Recover when a cached index still references a removed hashed CSS file after deploy. */
function watchMainStylesheet(): void {
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
    if (!/\/assets\/index-[^/]+\.css/i.test(link.href)) continue;
    link.addEventListener(
      "error",
      () => {
        try {
          if (sessionStorage.getItem(STYLE_RELOAD_KEY) === "1") return;
          sessionStorage.setItem(STYLE_RELOAD_KEY, "1");
        } catch {
          /* private mode */
        }
        location.reload();
      },
      { once: true },
    );
  }
  window.addEventListener(
    "load",
    () => {
      try {
        sessionStorage.removeItem(STYLE_RELOAD_KEY);
      } catch {
        /* private mode */
      }
    },
    { once: true },
  );
}
watchMainStylesheet();

const mapEl = must("#map");
const hintEl = must("#hint");
const toastEl = must("#toast");
const pointCountEl = must("#point-count");
const pointLabelEl = must("#point-label");
const distanceEl = must("#distance");
const etaEl = must("#eta");
const statsIssuesEl = must("#stats-issues");
const issueButtons: Array<[HTMLButtonElement, Exclude<RouteIssueKind, "part">]> = [
  [must<HTMLButtonElement>("#issue-gaps"), "gap"],
  [must<HTMLButtonElement>("#issue-overlaps"), "overlap"],
  [must<HTMLButtonElement>("#issue-crossings"), "crossing"],
];
const selectPartsBtn = must<HTMLButtonElement>("#select-parts-btn");
const titleInput = must<HTMLInputElement>("#title-input");
const googleBtn = must("#google-btn");
const meChip = must("#me-chip");
const meName = must("#me-name");
const meTrust = must("#me-trust");
const logoutBtn = must<HTMLButtonElement>("#logout-btn");
const undoBtn = must<HTMLButtonElement>("#undo-btn");
const redoBtn = must<HTMLButtonElement>("#redo-btn");
const clearBtn = must<HTMLButtonElement>("#clear-btn");
const saveBtn = must<HTMLButtonElement>("#save-btn");
const nearbyList = must("#nearby-list");
const nearbySearchAreaBtn = must<HTMLButtonElement>("#nearby-search-area");
const savedList = must("#saved-list");
const geojsonLink = must<HTMLAnchorElement>("#geojson-link");
const gpxLink = must<HTMLAnchorElement>("#gpx-link");
const viewSettingsBtn = must<HTMLButtonElement>("#view-settings-btn");
const pieceDetail = must("#piece-detail");
const pieceDetailTitle = must("#piece-detail-title");
const pieceDetailPoints = must("#piece-detail-points");
const pieceDetailPointsLabel = must("#piece-detail-points-label");
const pieceDetailStrip = must("#piece-detail-strip");
const questInlinePage = must("#quest-inline-page");
const pieceDetailDistance = must("#piece-detail-distance");
const pieceDetailMeta = must("#piece-detail-meta");
const pieceDetailStatus = must("#piece-detail-status");
const pieceDetailPublish = must<HTMLButtonElement>("#piece-detail-publish");
const pieceDetailEdit = must<HTMLButtonElement>("#piece-detail-edit");
const pieceDetailBadge = must("#piece-detail-badge");
const questDetailEdit = must<HTMLAnchorElement>("#quest-detail-edit");
const questDetailSaveHeart = must<HTMLButtonElement>("#quest-detail-save-heart");
const questDetailExpand = must<HTMLButtonElement>("#quest-detail-expand");
const questDetailClose = must<HTMLButtonElement>("#quest-detail-close");
const questPreviewThumb = must<HTMLImageElement>("#quest-preview-thumb");
const detailToggleBtn = must<HTMLButtonElement>("#detail-toggle-btn");
const detailMenuWrap = must("#detail-menu-wrap");
const detailMenu = bindPanelMenu(must<HTMLButtonElement>("#detail-menu-btn"), must("#detail-menu"), window);
const sheetMenu = bindPanelMenu(must<HTMLButtonElement>("#sheet-menu-btn"), must("#sheet-menu"), window);
fillLanguageMenu(must("#lang-menu"), () => sheetMenu.close());
const clearCacheBtn = must<HTMLButtonElement>("#clear-cache-btn");
const privacyLink = must<HTMLAnchorElement>("#privacy-link");
const homeLegalHost = must<HTMLElement>("#home-legal-host");
mountLegalFooter(homeLegalHost);
const pieceDetailBody = must("#piece-detail-body");
const magnetBtn = must<HTMLButtonElement>("#magnet-btn");
const pointsToggleBtn = must<HTMLButtonElement>("#points-toggle-btn");
const placeCenterBtn = must<HTMLButtonElement>("#place-center-btn");
const loopBadge = must("#loop-badge");
const repeatEl = must("#repeat");
const moveBtn = must<HTMLButtonElement>("#move-btn");
const joinBtn = must<HTMLButtonElement>("#join-btn");
const mergeBtn = must<HTMLButtonElement>("#merge-btn");
const deleteMenuBtn = must<HTMLButtonElement>("#delete-menu-btn");
const deleteSelectedBtn = must<HTMLButtonElement>("#delete-selected-btn");
const selectAllBtn = must<HTMLButtonElement>("#select-all-btn");
const deselectBtn = must<HTMLButtonElement>("#deselect-btn");
const deselectAllBtn = must<HTMLButtonElement>("#deselect-all-btn");
const copyStyleBtn = must<HTMLButtonElement>("#copy-style-btn");
const pasteStyleBtn = must<HTMLButtonElement>("#paste-style-btn");
const toggleDecorativeBtn = must<HTMLButtonElement>("#toggle-decorative-btn");
const styleDecorativeBtn = must<HTMLButtonElement>("#style-decorative-btn");
const styleDecorativeMixed = must("#style-decorative-mixed");
const selectToolBtn = must<HTMLButtonElement>("#select-tool-btn");
const boundaryBtn = must<HTMLButtonElement>("#boundary-btn");
const boundaryDoneBtn = must<HTMLButtonElement>("#boundary-done-btn");
const boundaryClearBtn = must<HTMLButtonElement>("#boundary-clear-btn");
const fillBtn = must<HTMLButtonElement>("#fill-btn");
const fillColorBtn = must<HTMLButtonElement>("#fill-color-btn");
const roadsBtn = must<HTMLButtonElement>("#roads-btn");
const roadsRow = must("#roads-row");
const spacingRow = must("#spacing-row");
const eraseSizeRow = must("#erase-size-row");
const pieceTabs = Array.from(document.querySelectorAll<HTMLButtonElement>(".piece-tab"));
const pieceTabPanels = Array.from(document.querySelectorAll<HTMLElement>(".piece-tab-panel"));
bindHomeTabs(pieceTabs, pieceTabPanels, (tab) => {
  if (tab === "mine") void refreshQuestList();
  if (tab === "saved") void refreshSavedList();
  if (tab === "nearby") {
    syncNearbyList();
    syncHomeListsCurrent();
    paintNearbySearchAreaButton();
  }
});
let nearbySearchAreaLoading = false;

nearbySearchAreaBtn.addEventListener("click", () => {
  if (nearbySearchAreaLoading) return;
  nearbySearchAreaLoading = true;
  paintNearbySearchAreaButton();
  void refreshNearby({ scrollTop: true }).finally(() => {
    nearbySearchAreaLoading = false;
    paintNearbySearchAreaButton();
  });
});
const eraseSizeLabel = must("#erase-size-label");
const eraseSizeValue = must("#erase-size-value");
const eraseSizeInput = must<HTMLInputElement>("#erase-size-input");
const eraseBtn = must<HTMLButtonElement>("#erase-btn");
const workAreaBtn = must<HTMLButtonElement>("#work-area-btn");
const workAreaClearBtn = must<HTMLButtonElement>("#work-area-clear-btn");
const spacingInput = must<HTMLInputElement>("#spacing-input");
const spacingValue = must("#spacing-value");
const settingsBtn = must<HTMLButtonElement>("#settings-btn");
const settingsBackdrop = must("#settings-backdrop");
const settingsTitle = must("#settings-title");
const settingsBack = must<HTMLButtonElement>("#settings-back");
const settingsClose = must<HTMLButtonElement>("#settings-close");
const settingsHome = must("#settings-home");
const openKeymapBtn = must<HTMLButtonElement>("#open-keymap");
const openGraphicsBtn = must<HTMLButtonElement>("#open-graphics");
const graphicsPanel = must("#graphics-panel");
const graphicsStatusValue = must("#graphics-status-value");
const graphicsRendererValue = must("#graphics-renderer-value");
const graphicsHelp = must("#graphics-help");
const graphicsToast = must("#graphics-toast");
const graphicsToastOpen = must<HTMLButtonElement>("#graphics-toast-open");
const graphicsToastDismiss = must<HTMLButtonElement>("#graphics-toast-dismiss");
const keymapPanel = must("#keymap-panel");
const keymapPreset = must("#keymap-preset");
const keymapList = must("#keymap-list");
const keymapWarn = must("#keymap-warn");
const keymapReset = must<HTMLButtonElement>("#keymap-reset");
const fillPalette = must("#fill-palette");
const fillPaletteSets = must("#fill-palette-sets");
const fillPaletteNone = must("#fill-palette-none");
const fillPaletteSwatches = must("#fill-palette-swatches");
const fillPaletteCustoms = must("#fill-palette-customs");
const fillColorInput = must<HTMLInputElement>("#fill-color-input");
const styleDock = must("#style-dock");
const stylePanel = must("#style-panel");
const stylePoints = must("#style-points");
const styleLines = must("#style-lines");
const stylePointSwatches = must("#style-point-swatches");
const styleLineSwatches = must("#style-line-swatches");
const styleLineSets = must("#style-line-sets");
const styleLineCustoms = must("#style-line-customs");
const stylePointSize = must<HTMLInputElement>("#style-point-size");
const styleLineWidth = must<HTMLInputElement>("#style-line-width");
const stylePointMixed = must("#style-point-mixed");
const styleLineMixed = must("#style-line-mixed");
const styleFaceSets = must("#style-face-sets");
const styleFaceNone = must("#style-face-none");
const styleFaceSwatches = must("#style-face-swatches");
const styleFaceCustoms = must("#style-face-customs");
const styleApply = must<HTMLButtonElement>("#style-apply");
const stylePatternButtons = [
  must<HTMLButtonElement>("#style-pattern-solid"),
  must<HTMLButtonElement>("#style-pattern-dashed"),
  must<HTMLButtonElement>("#style-pattern-dotted"),
];
const openStyleDefaultsBtn = must<HTMLButtonElement>("#open-style-defaults");
const styleDefaultsPanel = must("#style-defaults-panel");
const defaultPointSwatches = must("#default-point-swatches");
const defaultLineSwatches = must("#default-line-swatches");
const defaultLineSets = must("#default-line-sets");
const defaultLineCustoms = must("#default-line-customs");
const defaultPointSize = must<HTMLInputElement>("#default-point-size");
const defaultLineWidth = must<HTMLInputElement>("#default-line-width");
const defaultPatternButtons = [
  must<HTMLButtonElement>("#default-pattern-solid"),
  must<HTMLButtonElement>("#default-pattern-dashed"),
  must<HTMLButtonElement>("#default-pattern-dotted"),
];
const moreBtn = must<HTMLButtonElement>("#more-btn");
const deletePointBtn = must<HTMLButtonElement>("#delete-point-btn");
const deleteMenu = must<HTMLDivElement>("#delete-menu");
const deletePointsItem = must<HTMLButtonElement>("#delete-menu-points");
const deleteLinesItem = must<HTMLButtonElement>("#delete-menu-lines");
const deleteBothItem = must<HTMLButtonElement>("#delete-menu-both");
const setStartBtn = must<HTMLButtonElement>("#set-start-btn");
const importGpxBtn = must<HTMLButtonElement>("#import-gpx-btn");
const gpxFile = must<HTMLInputElement>("#gpx-file");
/** Flip to true to show the topbar snapshot control. Implementation stays in the tree. */
const SNAPSHOT_UI_ENABLED = false;
const snapshotBtn = must<HTMLButtonElement>("#snapshot-btn");
const snapshotSaveBtn = must<HTMLButtonElement>("#snapshot-save-btn");
const exitBtn = must<HTMLButtonElement>("#exit-btn");
const sheetEl = must("#sheet");
const publicPieceDelete = must<HTMLButtonElement>("#public-piece-delete");
const savePrompt = must("#save-prompt");
const savePromptSave = must<HTMLButtonElement>("#save-prompt-save");
const savePromptDiscard = must<HTMLButtonElement>("#save-prompt-discard");
const savePromptCancel = must<HTMLButtonElement>("#save-prompt-cancel");
const signinPrompt = must("#signin-prompt");
const workAreaPrompt = must("#work-area-prompt");
must<HTMLButtonElement>("#work-area-prompt-start").addEventListener("click", () => {
  workAreaPrompt.hidden = true;
  editor.setWorkMode(true);
  syncChrome(false);
});
must<HTMLButtonElement>("#work-area-prompt-later").addEventListener("click", () => {
  workAreaPrompt.hidden = true;
});
const signinPromptGoogle = must("#signin-prompt-google");
const signinPromptCancel = must<HTMLButtonElement>("#signin-prompt-cancel");
const appEl = must("#app");
const questRoot = must("#quest-root");
const questListItems = must("#quest-list-items");
const questListEmpty = must("#quest-list-empty");
const questCreateBtn = must<HTMLAnchorElement>("#quest-create-btn");
const activeQuestRoute = entryRoute && entryRoute.kind !== "route" ? entryRoute : null;
let questPage: { destroy(): void; flushPending?(): Promise<boolean> } | null = null;
let questRouteTitle = "";
/** Admin live-editing a published quest's route in this tab (save confirm once per session). */
let questRouteLiveEdit = false;
let questRoutePublished = false;

/** The published quest open in the detail panel, its route drawn on the map. */
let loadedQuest: QuestDetail | null = null;
/** List-card selection before a full quest detail fetch (nearby / saved / mine with preview). */
let listPreviewQuest: NearbyQuest | null = null;
let nearby: NearbyQuest[] = [];
let nearbyListAnchor: NearbyListAnchor | null = null;
let savedQuests: NearbyQuest[] = [];
/** Published rows from My quests that have a route preview (for lightweight card opens). */
let myListedQuests: NearbyQuest[] = [];
let detailSaveBusy = false;
const listPreview = new ListPreviewPanel(
  {
    body: pieceDetailBody,
    title: pieceDetailTitle,
    badge: pieceDetailBadge,
    distance: pieceDetailDistance,
    pointsLabel: pieceDetailPointsLabel,
    points: pieceDetailPoints,
    strip: pieceDetailStrip,
    meta: pieceDetailMeta,
    status: pieceDetailStatus,
    publish: pieceDetailPublish,
    edit: pieceDetailEdit,
    questEdit: questDetailEdit,
    expand: questDetailExpand,
    remove: publicPieceDelete,
    geojson: geojsonLink,
    gpx: gpxLink,
    saveHeart: questDetailSaveHeart,
    thumb: questPreviewThumb,
  },
  (run) => {
    whenMapHasStyle(editor.map, run);
  },
);
let sheetOpen = true;
/** Right detail drawer. Its handle stays visible while a piece is shown. */
let detailOpen = true;
/** Set by the drawer toggle (Tab). A newly shown piece keeps the detail drawer closed. */
let drawersCollapsed = false;
/** The open quest's page (the same one `/q/:id` shows), inline under its detail. */
let inlinePage: { key: string; view: RouteTraceEditor } | null = null;
/** Piece shown in the detail panel last paint, so a new piece can reopen the drawer. */
let detailPieceKey: string | null = null;
let roadsOpen = false;
let me: AuthUser | null = null;
let googleClientId: string | null = null;
let toastTimer = 0;
let geojsonUrl = "";
let session: EditorSession = createEditorSession();
let activeUserPieceId: string | null = null;
/** True when the open private Piece was published (locked). */
let activeUserPiecePublished = false;
/** The open own piece has a server copy (or came from one). */
let activeUserPieceOnServer = false;
/** Account that owns the open own piece; null for a signed-out local draft. */
let activeUserPieceOwner: string | null = null;
let lastSavedTitle = "";
let savedGraph: PieceGraph | null = null;
let pendingSaveAfterSignIn = false;
let pendingLeave: (() => void) | null = null;
let signinPromptOpen = false;
let draftDirty = false;
let draftGeneration = 0;
let draftIdle: number | null = null;
let draftTimer: ReturnType<typeof setTimeout> | null = null;
let routeIdle: number | null = null;
let routeTimer: ReturnType<typeof setTimeout> | null = null;

/** A few odds is a chain and finishes on this frame. A street grid is deferred so the pointer frame stays short. */
const ROUTE_SYNC_ODDS = 20;
const ROUTE_SYNC_POINTS = 1500;

/** Drops stale home list / draft open and loadById work after deselect or a newer card click. */
let homeListCardGen = 0;
/** Selected quest when edit mode was entered; restored if something cleared it on exit. */
let selectedQuestBeforeEdit: string | null = null;

function persistHomeBrowseReturn(opts: { questId?: string | null; returnToHome?: boolean } = {}): void {
  if (questRouteId || activeQuestRoute) return;
  const view = editor.captureBrowseViewForPersist();
  if (!view) return;
  writeHomeBrowseReturn({
    questId: opts.questId ?? selectedQuestId(),
    view,
    returnToHome: opts.returnToHome ?? true,
  });
}

function homeReturnHref(questId: string, stored: HomeBrowseReturn | null = readHomeBrowseReturn()): string {
  if (stored?.returnToHome && stored.view) {
    return formatHomeBrowseUrl(stored.questId ?? questId, stored.view);
  }
  return formatHomeBrowseUrl(questId, null);
}

/** Apply URL or sessionStorage home return before nearby / default camera logic. */
async function applyHomeBrowseBoot(url: URL): Promise<void> {
  const fromUrl = parseHomeBrowseSearch(url.search);
  const stored = fromUrl.view ? null : readHomeBrowseReturn();
  const questId = fromUrl.questId ?? url.searchParams.get("id") ?? stored?.questId ?? null;
  const view = fromUrl.view
    ? { ...fromUrl.view, framed: false }
    : stored?.returnToHome
      ? stored.view
      : null;
  const restoring = Boolean(view || (stored?.returnToHome && questId));

  if (view) {
    editor.applyPersistedBrowseView(view);
    await editor.camera.whenIdle();
  }
  if (stored?.returnToHome) clearHomeBrowseReturn();
  if (fromUrl.view) history.replaceState(null, "", stripHomeBrowseViewFromUrl(location.href));

  if (questId) {
    await loadById(questId, {
      quiet: true,
      fit: !view,
      panToStart: false,
      listOpenGen: homeListCardGen,
      holdCamera: Boolean(view),
    });
  } else if (!view && questId && restoring) {
    editor.showQuestStart(0);
  }
  await editor.camera.whenIdle();
}

const editor = new MapEditor(mapEl, syncChrome, (id) => {
  void loadById(id, { fit: true });
}, showToast);
let homeProgrammaticMoveCount = 0;
editor.map.on("movestart", (e) => {
  if (questRouteId || activeQuestRoute || session.mode !== "view") return;
  if (!e.originalEvent) homeProgrammaticMoveCount += 1;
});
editor.onHomeQuestFitStart = () => {
  homeProgrammaticMoveCount = 0;
};
const nearbyViewer = createNearbyViewer(() => editor.map);
editor.onBrowseMapBackgroundClick = () => clearHomeQuestSelection();
editor.onHomeQuestSpotSelect = (index) => {
  inlinePage?.view.selectSpotFromMap(index);
};
editor.onBrowseMapUserChanged = () => scheduleNearbyMapSync();
editor.onRestoreBrowseAfterEdit = () => {
  const id = selectedQuestBeforeEdit;
  selectedQuestBeforeEdit = null;
  if (id && selectedQuestId() !== id) {
    void loadById(id, { quiet: true, fit: false, listOpenGen: homeListCardGen, panToStart: false });
  }
};
editor.onMyLocationArrived = () => {
  void refreshNearby({ scrollTop: false });
};
/** Read-only handle for headless live checks (`scripts/verify-home-quest-route-style-live.mjs`). */
onAnyRouteSaved((questId) => {
  if (questRouteId || document.body.classList.contains("quest-mode")) return;
  void refreshNearby();
  if (loadedQuest?.id !== questId) return;
  void editor.camera.whenIdle().then(() => {
    if (loadedQuest?.id !== questId) return;
    void loadById(questId, { quiet: true, listOpenGen: homeListCardGen });
  });
});

Object.assign(window, {
  __mapusHomeMap: editor.map,
  __mapusHomeEditor: editor,
  __mapusHomeMoveCount: () => homeProgrammaticMoveCount,
  __mapusHomeCameraWhenIdle: () => editor.cameraWhenIdle(),
  __mapusHomeCameraProgrammaticActive: () => editor.cameraProgrammaticActive(),
  __mapusListPreviewMapPoint: () => (listPreviewQuest ? questMapPoint(listPreviewQuest) : null),
  __mapusNearbyListOrder: () => nearby.map((q) => q.id),
  __mapusSelectedQuestId: () => selectedQuestId(),
  __mapusNearbySearchAreaVisible: () => !nearbySearchAreaBtn.hidden,
  __mapusNearbyListAnchorReady: () => nearbyListAnchor != null,
  __mapusNearbyViewerAt: () => nearbyViewer.at(),
  __mapusPersistHomeBrowseReturn: (opts?: { questId?: string | null; returnToHome?: boolean }) =>
    persistHomeBrowseReturn(opts ?? {}),
  __mapusReadHomeBrowseReturn: () => readHomeBrowseReturn(),
});
editor.onCompassPress = () => runAction("northUp");
for (const root of [
  sheetEl,
  pieceDetail,
  styleDock,
  must("#edit-meta"),
  settingsBackdrop,
  deleteMenu,
  fillPalette,
  signinPrompt,
  savePrompt,
  workAreaPrompt,
]) {
  bindMapOverlayWheelIsolation(root);
}

/** Live overlay rects in map pixels. Read at fit time, after the detail panel has laid out. */
function homeMapOverlayRects(): { width: number; height: number; panels: ScreenRect[] } {
  const map = mapEl.getBoundingClientRect();
  const panels: ScreenRect[] = [];
  const add = (el: Element | null): void => {
    if (!(el instanceof HTMLElement) || el.hidden || el.getClientRects().length === 0) return;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    panels.push({ left: r.left - map.left, top: r.top - map.top, right: r.right - map.left, bottom: r.bottom - map.top });
  };
  add(sheetEl);
  add(pieceDetail);
  add(mapEl.querySelector(".maplibregl-ctrl-top-right"));
  add(mapEl.querySelector(".maplibregl-ctrl-bottom-right"));
  add(mapEl.querySelector(".maplibregl-ctrl-bottom-left"));
  return { width: map.width, height: map.height, panels };
}

editor.setViewInsets(() => {
  const { width, height, panels } = homeMapOverlayRects();
  return splitOverlayInsets(width, height, panels).insets;
});
editor.setViewObstacles(() => {
  const { width, height, panels } = homeMapOverlayRects();
  return splitOverlayInsets(width, height, panels).corners;
});
const inputPlatform: Platform = detectPlatform(navigator);
let storedKeys: StoredKeymap = readStoredKeys();
editor.setKeymap(storedKeys.keymap, inputPlatform);
editor.setStyleDefaults(readStyleDefaults());
editor.setDeleteMenuDismiss(() => closeDeleteMenu());
let settingsOpen = false;
let deleteMenuOpen = false;
let deleteHighlight: DeleteMenuChoice = "points";
let deleteHintUntil = 0;
let selectToolHintUntil = 0;
let actionHintUntil = 0;
let actionHint = "";
const FILL_PALETTE_KEY = "mapus.fillRegion.palette";
const FILL_COLOR_KEY = "mapus.fillRegion.color";
const FILL_CUSTOMS_KEY = "mapus.fillRegion.customs";
let fillPaletteOpen = false;
let fillPaletteId: FillPaletteId = readFillPalette();
let fillSwatchIndex = 0;
let fillColor = readFillColor();
let customSlots = readCustomSlots();
/** Slot waiting on the native picker. Shared across palette sets. */
let customPickSlot: number | null = null;
type CustomPickTarget = "brush" | "faces" | "line" | "defaultLine";

/** Brush keeps the F tool. Faces paints every closed face from the Style panel. */
let customPickTarget: CustomPickTarget = "brush";
/** Bumped when the shared palette changes, so the Style panel can repaint. */
let paletteGeneration = 0;
let styleFaceGeneration = -1;
let styleLineGeneration = -1;
let defaultLineGeneration = -1;
/** Line color chosen in the Style panel before Apply. */
let lineStyleColor = readStyleDefaults().line.color;
/** Default line color in Settings → Style defaults. */
let defaultLineStyleColor = readStyleDefaults().line.color;
editor.setRegionColor(fillColor);
fillColorInput.value = fillColor === NO_FILL ? DEFAULT_FILL_COLOR : fillColor;
fillColorInput.addEventListener("change", () => {
  const hex = normalizeColor(fillColorInput.value);
  if (!hex) return;
  const slot = customPickSlot;
  const target = customPickTarget;
  customPickSlot = null;
  customPickTarget = "brush";
  if (slot != null && slot >= 0 && slot < CUSTOM_SLOT_COUNT) {
    customSlots[slot] = hex;
    writeCustomSlots();
    if (target === "faces") {
      refreshPalettes();
      editor.fillEveryFace(hex);
      return;
    }
    if (target === "line") {
      lineStyleColor = hex;
      refreshPalettes();
      return;
    }
    if (target === "defaultLine") {
      defaultLineStyleColor = hex;
      refreshPalettes();
      saveStyleDefaultsFromForm();
      return;
    }
    fillSwatchIndex = paletteSelectionIndex(hex, fillPaletteId, customSlots);
    refreshPalettes();
    chooseFillColor(hex);
    return;
  }
  if (target === "faces") {
    editor.fillEveryFace(hex);
    return;
  }
  if (target === "line") {
    lineStyleColor = hex;
    refreshPalettes();
    return;
  }
  if (target === "defaultLine") {
    defaultLineStyleColor = hex;
    refreshPalettes();
    saveStyleDefaultsFromForm();
    return;
  }
  chooseFillColor(hex);
});
let keymapCapture: { action: ActionId; index: number } | null = null;
let keymapIgnorePointer: number | null = null;
const snapshot = new SnapshotOverlay(must("#app"), editor.map);

if (SNAPSHOT_UI_ENABLED) {
  snapshotBtn.hidden = false;
  snapshotBtn.addEventListener("click", () => {
    const next = !snapshot.isActive();
    snapshot.setActive(next);
    snapshotBtn.setAttribute("aria-pressed", String(next));
    snapshotBtn.textContent = next ? t("snapshot.close") : t("snapshot.open");
    snapshotSaveBtn.hidden = !next;
  });

  snapshotSaveBtn.addEventListener("click", () => {
    if (!snapshot.isActive()) return;
    const fc = snapshot.saveRoadGeoJSON();
    showToast(
      fc.features.length > 0
        ? t("snapshot.saved", { count: fc.features.length })
        : t("snapshot.none"),
    );
  });
} else {
  snapshotBtn.hidden = true;
  snapshotSaveBtn.hidden = true;
  snapshot.setActive(false);
}

let homeBootQuest: Promise<void> | null = null;
{
  const bootUrl = new URL(location.href);
  const bootId = bootUrl.searchParams.get("id");
  const bootPiece = bootUrl.searchParams.get("piece");
  if (bootId && !bootPiece && !questRouteId) homeBootQuest = loadById(bootId, { fit: true });
}

editor.map.on("load", () => {
  if (questRouteId) {
    void openQuestRoute(questRouteId);
    return;
  }
  // Quest edit/view shell: do not run home boot or consume browse return on the hidden map.
  if (activeQuestRoute) return;
  const url = new URL(location.href);
  const pieceId = url.searchParams.get("piece");
  const questId = url.searchParams.get("id");
  const storedBrowse = readHomeBrowseReturn();
  const browseBoot =
    Boolean(parseHomeBrowseSearch(url.search).view) ||
    (storedBrowse?.returnToHome === true && !pieceId);

  const finishHomeBoot = (): void => {
    void migrateDraft();
    applySession();
    syncChrome(false);
  };

  const startNearby = (): void => {
    nearbyViewer.start(() => {
      void refreshNearby();
    });
    void refreshNearby();
  };

  void pieceStore.clearPieceCache().catch(() => undefined);

  if (pieceId) {
    startNearby();
    void openUserPiece(pieceId).finally(finishHomeBoot);
    return;
  }

  if (browseBoot) {
    void applyHomeBrowseBoot(url).then(() => {
      startNearby();
      finishHomeBoot();
    });
    return;
  }

  if (questId) {
    void (homeBootQuest ?? loadById(questId, { fit: true })).finally(() => {
      startNearby();
      finishHomeBoot();
    });
    return;
  }

  startNearby();
  if (storedBrowse) clearHomeBrowseReturn();
  finishHomeBoot();
});

editor.map.on("zoom", () => updateHint());

/** User pan/zoom only — programmatic quest reveals use the camera queue without originalEvent. */
let browsePersistTimer: ReturnType<typeof setTimeout> | null = null;
editor.map.on("moveend", (e) => {
  if (e.originalEvent) scheduleNearbyMapSync();
  if (questRouteId || activeQuestRoute || session.mode !== "view") return;
  if (!selectedQuestId()) return;
  if (browsePersistTimer != null) clearTimeout(browsePersistTimer);
  browsePersistTimer = setTimeout(() => persistHomeBrowseReturn({ returnToHome: true }), 400);
});
editor.map.on("zoomend", (e) => {
  if (e.originalEvent) scheduleNearbyMapSync();
});

undoBtn.addEventListener("click", () => runAction("undo"));
redoBtn.addEventListener("click", () => runAction("redo"));
magnetBtn.addEventListener("click", () => runAction("toggleSnap"));
pointsToggleBtn.addEventListener("click", () => runAction("togglePoints"));
placeCenterBtn.addEventListener("click", () => editor.placeAtCenter());
moveBtn.addEventListener("click", () => runAction("moveSelected"));
joinBtn.addEventListener("click", () => runAction("join"));
mergeBtn.addEventListener("click", () => runAction("merge"));
deleteMenuBtn.addEventListener("click", () => runAction("deleteMenu"));
deleteSelectedBtn.addEventListener("click", () => runAction("deleteSelected"));
selectAllBtn.addEventListener("click", () => runAction("selectAll"));
deselectBtn.addEventListener("click", () => runAction("deselect"));
deselectAllBtn.addEventListener("click", () => runAction("deselectAll"));
selectPartsBtn.addEventListener("click", () => {
  editor.selectSmallerParts();
});
const issueCursor: Record<RouteIssueKind, number> = { part: 0, gap: 0, overlap: 0, crossing: 0 };
function focusNextIssue(kind: RouteIssueKind): void {
  editor.focusIssue(kind, issueCursor[kind]);
  issueCursor[kind] += 1;
}
for (const [btn, kind] of issueButtons) btn.addEventListener("click", () => focusNextIssue(kind));
distanceEl.addEventListener("click", () => {
  if (distanceEl.classList.contains("stats-warn")) focusNextIssue("part");
});
distanceEl.addEventListener("keydown", (e) => {
  if (!distanceEl.classList.contains("stats-warn") || (e.key !== "Enter" && e.key !== " ")) return;
  e.preventDefault();
  focusNextIssue("part");
});
copyStyleBtn.addEventListener("click", () => runAction("copyStyle"));
pasteStyleBtn.addEventListener("click", () => runAction("pasteStyle"));
toggleDecorativeBtn.addEventListener("click", () => runAction("toggleDecorative"));
styleDecorativeBtn.addEventListener("click", () => runAction("toggleDecorative"));
selectToolBtn.addEventListener("click", () => runAction("cycleSelectTool"));
boundaryBtn.addEventListener("click", () => cycleRoadsTool());
boundaryDoneBtn.addEventListener("click", () => editor.closeBoundary());
boundaryClearBtn.addEventListener("click", () => {
  if (roadsOpen) editor.clearBoundary();
  syncChrome(false);
});
fillBtn.addEventListener("click", () => {
  if (roadsOpen) editor.fillRoads();
  syncChrome(false);
});
roadsBtn.addEventListener("click", () => runAction("fillRoadsTool"));

/** Switching to another tool (Erase, Canvas, Fill Color) closes Fill Roads, as R would. */
function closeRoadsForTool(): void {
  if (!roadsOpen) return;
  roadsOpen = false;
  editor.leaveRoadsFill();
  syncChrome(false);
}

function toggleRoadsTool(): void {
  roadsOpen = !roadsOpen;
  if (roadsOpen) {
    sheetOpen = true;
    editor.enterRoadsFill();
  } else {
    editor.leaveRoadsFill();
  }
  syncSheetOpen();
  syncChrome(false);
}
fillColorBtn.addEventListener("click", () => runAction("fillTool"));
eraseBtn.addEventListener("click", () => runAction("erase"));
workAreaBtn.addEventListener("click", () => runAction("workArea"));
workAreaClearBtn.addEventListener("click", () => runAction("clearWorkArea"));
eraseSizeInput.addEventListener("input", () => {
  editor.setEraseBrushRadius(Number(eraseSizeInput.value));
  eraseSizeValue.textContent = String(Math.round(editor.eraseBrushRadius()));
});
spacingInput.addEventListener("input", () => {
  editor.previewSpacing(Number(spacingInput.value));
  spacingValue.textContent = t("spacing.meters", { meters: editor.spacingMeters() });
});
spacingInput.addEventListener("change", () => {
  editor.previewSpacing(Number(spacingInput.value));
  editor.commitSpacing();
});
deletePointBtn.addEventListener("click", () => {
  editor.deleteSelected();
});
deletePointsItem.addEventListener("pointerenter", () => highlightDeleteItem("points"));
deleteLinesItem.addEventListener("pointerenter", () => highlightDeleteItem("lines"));
deleteBothItem.addEventListener("pointerenter", () => highlightDeleteItem("pointsAndLines"));
deletePointsItem.addEventListener("click", () => confirmDeleteChoice("points"));
deleteLinesItem.addEventListener("click", () => confirmDeleteChoice("lines"));
deleteBothItem.addEventListener("click", () => confirmDeleteChoice("pointsAndLines"));
deleteMenu.addEventListener("contextmenu", (event) => {
  event.preventDefault();
  closeDeleteMenu();
});
paintDeleteAccelerators();
window.addEventListener("pointerdown", onDeleteMenuPointer, true);
setStartBtn.addEventListener("click", () => {
  editor.setStartToSelected();
});
importGpxBtn.addEventListener("click", () => gpxFile.click());
gpxFile.addEventListener("change", () => {
  const file = gpxFile.files?.[0];
  gpxFile.value = "";
  if (file) void onGpx(file);
});
moreBtn.addEventListener("click", () => {
  sheetOpen = !sheetOpen;
  if (sheetOpen) drawersCollapsed = false;
  syncSheetOpen();
});

/** Gap between home map controls and the right detail panel (px). */
const MAP_CTRL_PANEL_GAP = 8;
const MAP_CTRL_RIGHT_BASE = 10;

/** Sync CSS vars so bottom-right map controls sit beside the detail panel, not under it. */
function syncMapControlChrome(): void {
  const rect = pieceDetail.getBoundingClientRect();
  appEl.style.setProperty("--right-panel-h", `${Math.round(rect.height)}px`);

  let right = MAP_CTRL_RIGHT_BASE;
  const mobile = window.matchMedia("(max-width: 720px)").matches;
  if (session.mode === "view" && detailOpen && !pieceDetailBody.hidden && !mobile) {
    right = Math.max(MAP_CTRL_RIGHT_BASE, Math.round(window.innerWidth - rect.left + MAP_CTRL_PANEL_GAP));
  }
  appEl.style.setProperty("--home-map-ctrl-right", `${right}px`);
}

// Keep home map controls and attribution clear of the right detail panel (height on mobile, width on desktop).
new ResizeObserver(() => syncMapControlChrome()).observe(pieceDetail);
window.addEventListener("resize", () => syncMapControlChrome());

detailToggleBtn.addEventListener("click", () => {
  detailOpen = !detailOpen;
  if (detailOpen) drawersCollapsed = false;
  syncDetailOpen();
});
clearBtn.addEventListener("click", () => {
  if (editor.getCoordinates().length === 0) return;
  if (!window.confirm(t("confirm.clear"))) return;
  editor.clear();
});
// Detail panel Delete: a quest you wrote (or may delete), or your own open draft route.
publicPieceDelete.addEventListener("click", () => {
  if (loadedQuest != null) {
    if (templateDeletable(loadedQuest, me?.id ?? null)) void deleteLoadedQuest();
    return;
  }
  if (activeUserPieceId != null && ownPieceDeletable(activePieceAccess())) void deleteOwnPiece(activeUserPieceId);
});
function openHomeListSelection(click: QuestListClick): void {
  if (selectedQuestId() === click.id) {
    clearHomeQuestSelection();
    return;
  }
  const item = findHomeListQuest(click.id);
  const gen = ++homeListCardGen;
  if (item) showListQuestPreview(item);
  const holdCamera = editor.questViewIsFramed();
  void leaveThen(() => {
    if (click.draft) {
      return openOwnDraft(click.id, {
        fit: !holdCamera,
        listOpenGen: gen,
        quiet: true,
        panToStart: false,
        holdCamera,
      });
    }
    return loadById(click.id, {
      fit: !holdCamera,
      quiet: true,
      panToStart: false,
      listOpenGen: gen,
      holdCamera,
    });
  });
}

nearbyList.addEventListener("click", (event) => {
  if (event.target instanceof Element && event.target.closest("#nearby-new-quest")) guardNewQuest(event);
});
bindQuestListClicks(nearbyList, openHomeListSelection);
bindQuestListClicks(savedList, openHomeListSelection);
bindQuestListClicks(questListItems, openHomeListSelection);
pieceDetailEdit.addEventListener("click", () => {
  runAction("enterEdit");
});
pieceDetailPublish.addEventListener("click", () => {
  void onPublish();
});
clearCacheBtn.addEventListener("click", () => void clearCachedData());
viewSettingsBtn.addEventListener("click", () => {
  openSettings("home");
});
exitBtn.addEventListener("click", () => {
  runAction("exitEdit");
});
savePromptSave.addEventListener("click", () => {
  void resolveLeave("save");
});
savePromptDiscard.addEventListener("click", () => {
  void resolveLeave("discard");
});
savePromptCancel.addEventListener("click", () => {
  void resolveLeave("cancel");
});
signinPromptCancel.addEventListener("click", () => {
  pendingSaveAfterSignIn = false;
  signinPromptOpen = false;
  applySession();
});
saveBtn.addEventListener("click", () => runAction("savePiece"));
logoutBtn.addEventListener("click", () => {
  void onLogout();
});
titleInput.addEventListener("input", () => {
  markDirty();
});

settingsBtn.addEventListener("click", () => {
  openSettings("home");
});
const themeOptions = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-theme-option]"));
for (const option of themeOptions) {
  option.addEventListener("click", () => {
    const next = option.dataset.themeOption;
    if (!isPanelTheme(next)) return;
    panelTheme = next;
    applyPanelTheme(document.documentElement, panelTheme);
    writePanelTheme(safeLocalStorage(), panelTheme);
    paintThemeOptions();
  });
}
paintThemeOptions();

function paintThemeOptions(): void {
  for (const option of themeOptions) {
    option.setAttribute("aria-checked", String(option.dataset.themeOption === panelTheme));
  }
}

/** Range tracks paint their filled part from --range-fill (0..1); value changes in code repaint too. */
function paintRange(input: HTMLInputElement): void {
  const fraction = rangeFraction(Number(input.value), Number(input.min || 0), Number(input.max || 100));
  input.style.setProperty("--range-fill", String(fraction));
}

function paintRanges(): void {
  document.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach(paintRange);
}
document.addEventListener(
  "input",
  (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === "range") paintRange(target);
  },
  true,
);
paintRanges();
openKeymapBtn.addEventListener("click", () => openSettings("keymap"));
openGraphicsBtn.addEventListener("click", () => openSettings("graphics"));
openStyleDefaultsBtn.addEventListener("click", () => openSettings("style"));
graphicsToastOpen.addEventListener("click", () => {
  dismissGraphicsToast();
  openSettings("graphics");
});
graphicsToastDismiss.addEventListener("click", () => dismissGraphicsToast());
settingsBack.addEventListener("click", () => openSettings("home"));
settingsClose.addEventListener("click", () => closeSettings());
settingsBackdrop.addEventListener("click", (e) => {
  if (e.target === settingsBackdrop) closeSettings();
});
keymapReset.addEventListener("click", () => {
  storedKeys = resetStoredKeys();
  editor.setKeymap(storedKeys.keymap, inputPlatform);
  keymapCapture = null;
  keymapIgnorePointer = null;
  keymapWarn.hidden = true;
  renderKeymap();
  paintDeleteAccelerators();
  paintCommandLabels();
});
window.addEventListener("pagehide", () => {
  flushDraftSave();
});
window.addEventListener("beforeunload", (event) => {
  if (session.mode === "edit" && session.dirty) {
    event.preventDefault();
    event.returnValue = "";
  }
});
window.addEventListener("keydown", onKeyDown, true);
window.addEventListener("keyup", onKeyUp, true);
window.addEventListener("pointerdown", onCapturePointerDown, true);
window.addEventListener("pointermove", onCapturePointerMove, true);
window.addEventListener("pointerup", onCapturePointerUp, true);
window.addEventListener("wheel", onCaptureWheel, { capture: true, passive: false });

function onKeyDown(e: KeyboardEvent): void {
  if (isBareAlt(e)) e.preventDefault();
  if (keymapCapture) {
    e.preventDefault();
    e.stopPropagation();
    if (e.code === "Escape") {
      keymapCapture = null;
      renderKeymap();
      return;
    }
    const binding = bindingFromKey(keyState(e), inputPlatform);
    if (binding) assignBinding(keymapCapture.action, keymapCapture.index, binding);
    return;
  }
  if (settingsOpen) return;
  if (basemapToggleFromKey(e, { settingsOpen, keymapCapture: !!keymapCapture })) return;
  if (streetViewOpenFromKey(e, { settingsOpen, keymapCapture: !!keymapCapture })) return;
  // Quest pages hide the home map; only basemap toggle and typing targets apply there.
  if (document.body.classList.contains("quest-mode")) return;
  if (isTyping(e.target, e.code) && e.target !== fillColorInput) return;
  if (!editingActive(session) && e.code === "Escape" && selectedQuestId()) {
    e.preventDefault();
    clearHomeQuestSelection();
    return;
  }
  if (!editingActive(session)) {
    const action = matchKey(storedKeys.keymap, keyState(e), inputPlatform);
    if (!action) return;
    if (action === "savePiece" || action === "exitEdit") {
      e.preventDefault();
      return;
    }
    if (!actionAllowedInSession(session, action)) return;
    e.preventDefault();
    if (!e.repeat) runAction(action);
    return;
  }
  if (
    (fillPaletteOpen || editor.interactionMode() === "fill") &&
    actionMatchesKey(storedKeys.keymap, "fillTool", keyState(e), inputPlatform)
  ) {
    e.preventDefault();
    if (!e.repeat) toggleFillTool();
    return;
  }
  if (fillPaletteOpen) {
    e.preventDefault();
    const count = paletteCells(fillPaletteId, customSlots).length;
    if (e.code === "Escape") closeFillPalette();
    else if (e.code === "Enter" || e.code === "NumpadEnter") activateFillCell();
    else if (e.code === "ArrowRight") fillSwatchIndex = (fillSwatchIndex + 1) % count;
    else if (e.code === "ArrowLeft") fillSwatchIndex = (fillSwatchIndex - 1 + count) % count;
    else if (e.code === "ArrowDown") {
      if (fillSwatchIndex === 0) fillSwatchIndex = 1;
      else fillSwatchIndex = Math.min(count - 1, fillSwatchIndex + PALETTE_COLUMNS);
    } else if (e.code === "ArrowUp") {
      if (fillSwatchIndex === 0) {
        /* stay on No fill */
      } else if (fillSwatchIndex <= PALETTE_COLUMNS) fillSwatchIndex = 0;
      else fillSwatchIndex -= PALETTE_COLUMNS;
    }
    if (e.code.startsWith("Arrow")) paintFillPalette();
    return;
  }
  if (editor.interactionMode() === "fill" && actionMatchesKey(storedKeys.keymap, "exitFill", keyState(e), inputPlatform)) {
    e.preventDefault();
    editor.setFillMode(false);
    updateHint();
    return;
  }
  if (editor.isGrabbing()) {
    e.preventDefault();
    const state = keyState(e);
    if (actionMatchesKey(storedKeys.keymap, "confirmGrab", state, inputPlatform)) editor.confirmGrab();
    else if (actionMatchesKey(storedKeys.keymap, "cancelGrab", state, inputPlatform)) editor.cancelGrab();
    return;
  }
  if (deleteMenuOpen) {
    e.preventDefault();
    const state = keyState(e);
    if (actionMatchesKey(storedKeys.keymap, "cancelDeleteMenu", state, inputPlatform)) closeDeleteMenu();
    else if (actionMatchesKey(storedKeys.keymap, "confirmDeleteMenu", state, inputPlatform)) confirmDeleteChoice(deleteHighlight);
    else if (actionMatchesKey(storedKeys.keymap, "deleteMenuNext", state, inputPlatform)) stepDeleteHighlight(1);
    else if (actionMatchesKey(storedKeys.keymap, "deleteMenuPrev", state, inputPlatform)) stepDeleteHighlight(-1);
    else if (actionMatchesKey(storedKeys.keymap, "deleteMenuPoints", state, inputPlatform)) confirmDeleteChoice("points");
    else if (actionMatchesKey(storedKeys.keymap, "deleteMenuLines", state, inputPlatform)) confirmDeleteChoice("lines");
    else if (actionMatchesKey(storedKeys.keymap, "deleteMenuPointsAndLines", state, inputPlatform)) confirmDeleteChoice("pointsAndLines");
    return;
  }
  if (roadsOpen && session.mode === "edit") {
    const rs = keyState(e);
    const roadAction = (["cycleRoadsTool", "roadsClear", "roadsFill"] as const).find((a) =>
      actionMatchesKey(storedKeys.keymap, a, rs, inputPlatform),
    );
    if (roadAction) {
      e.preventDefault();
      if (!e.repeat) {
        if (roadAction === "cycleRoadsTool") cycleRoadsTool();
        else if (roadAction === "roadsClear") editor.clearBoundary();
        else editor.fillRoads();
        syncChrome(false);
      }
      return;
    }
  }
  const action = matchKey(storedKeys.keymap, keyState(e), inputPlatform);
  if (!action) return;
  if (action === "savePiece") {
    e.preventDefault();
    if (editingActive(session) && !e.repeat) void onSave({ stayInEdit: true });
    return;
  }
  if (action === "exitEdit") {
    e.preventDefault();
    if (editingActive(session) && !e.repeat) void leaveEdit("view");
    return;
  }
  if (action === "enterEdit") {
    e.preventDefault();
    return;
  }
  if (!actionAllowedInSession(session, action)) return;
  e.preventDefault();
  if (action === "deleteSelected" && !editor.hasSelection()) return;
  runAction(action);
}

function onKeyUp(e: KeyboardEvent): void {
  if (isBareAlt(e)) e.preventDefault();
}

function isBareAlt(e: KeyboardEvent): boolean {
  if (e.ctrlKey || e.metaKey) return false;
  return e.key === "Alt" || e.code === "AltLeft" || e.code === "AltRight";
}

function runAction(action: ActionId): void {
  if (!actionAllowedInSession(session, action)) return;
  if (action === "savePiece") {
    void onSave({ stayInEdit: true });
    return;
  }
  if (action === "exitEdit") {
    if (questRouteId) void finishQuestRoute(questRouteId);
    else void leaveEdit("view");
    return;
  }
  if (action === "enterEdit") {
    enterEditMode();
    return;
  }
  if (action === "undo") editor.undo();
  else if (action === "redo") editor.redo();
  else if (action === "placeAtCenter") editor.placeAtCursor();
  else if (action === "deselect") {
    if (editor.interactionMode() !== "graph") {
      editor.setBoundaryMode(false);
      editor.setEraseMode(false);
      editor.setWorkMode(false);
      editor.setFillMode(false);
    }
    editor.deselect();
  } else if (action === "selectAll") editor.selectAll();
  else if (action === "deselectAll") editor.deselectAll();
  else if (action === "cycleSelectTool") cycleSelectTool();
  else if (action === "deleteSelected") editor.deleteSelected();
  else if (action === "deleteMenu") openDeleteMenu();
  else if (action === "moveSelected") editor.beginGrabMove();
  else if (action === "join") {
    if (editor.joinSelected() === "few") flashActionHint("hint.joinFew");
  } else if (action === "merge") {
    if (editor.mergeSelected() === "few") flashActionHint("hint.mergeFew");
  } else if (action === "copyStyle") editor.copySelectionStyle();
  else if (action === "pasteStyle") editor.pasteSelectionStyle();
  else if (action === "toggleDecorative") editor.toggleDecorative();
  else if (action === "fillTool") toggleFillTool();
  else if (action === "erase") {
    const on = editor.interactionMode() !== "erase";
    if (on) closeRoadsForTool();
    editor.setEraseMode(on);
  } else if (action === "togglePoints") editor.setPointsVisible(!editor.pointsVisible());
  else if (action === "fillRoadsTool") toggleRoadsTool();
  else if (action === "workArea") {
    const on = editor.interactionMode() !== "work";
    if (on) closeRoadsForTool();
    editor.setWorkMode(on);
    syncChrome(false);
  } else if (action === "clearWorkArea") {
    const had = editor.hasWorkArea();
    editor.clearWorkArea();
    syncChrome(false);
    if (had) showToast(t("toast.canvasCleared", { undo: undoHint(inputPlatform) }));
  } else if (action === "toggleSnap") {
    editor.setMagnet(!editor.magnetEnabled());
    syncChrome(false);
  }
  else if (action === "zoomIn") editor.zoomBy(1);
  else if (action === "zoomOut") editor.zoomBy(-1);
  else if (action === "zoomInMore") editor.zoomBy(2);
  else if (action === "zoomOutMore") editor.zoomBy(-2);
  else if (action === "panLeft") editor.keyboardPan(-1, 0);
  else if (action === "panRight") editor.keyboardPan(1, 0);
  else if (action === "panUp") editor.keyboardPan(0, -1);
  else if (action === "panDown") editor.keyboardPan(0, 1);
  else if (action === "northUp") {
    const step = editor.northUp();
    if (step === "north") showToast(t("toast.north"));
    else if (step === "restore") showToast(t("toast.northBack"));
    else if (step === "canvas") showToast(t("toast.northRoute"));
  }
  else if (action === "toggleDrawers") toggleDrawers();
}

/** Tab: close both side drawers, or open both when they are already closed. */
function toggleDrawers(): void {
  const anyOpen = sheetOpen || detailOpen;
  sheetOpen = !anyOpen;
  detailOpen = !anyOpen;
  drawersCollapsed = anyOpen;
  syncSheetOpen();
  syncDetailOpen();
}

function deleteItem(choice: DeleteMenuChoice): HTMLButtonElement {
  if (choice === "points") return deletePointsItem;
  if (choice === "lines") return deleteLinesItem;
  return deleteBothItem;
}

const DELETE_CHOICES = ["points", "lines", "pointsAndLines"] as const;

function enabledDeleteChoices(): DeleteMenuChoice[] {
  const state = editor.deleteMenuState();
  return DELETE_CHOICES.filter((choice) => state[choice]);
}

function paintDeleteMenu(): void {
  const state = editor.deleteMenuState();
  deletePointsItem.disabled = !state.points;
  deleteLinesItem.disabled = !state.lines;
  deleteBothItem.disabled = !state.pointsAndLines;
  paintDeleteAccelerators();
  for (const choice of DELETE_CHOICES) {
    const item = deleteItem(choice);
    if (deleteHighlight === choice && state[choice]) item.setAttribute("aria-current", "true");
    else item.removeAttribute("aria-current");
  }
}

function paintDeleteAccelerators(): void {
  paintDeleteAccelerator(deletePointsItem, "delete.points", "deleteMenuPoints");
  paintDeleteAccelerator(deleteLinesItem, "delete.lines", "deleteMenuLines");
  paintDeleteAccelerator(deleteBothItem, "delete.pointsAndLines", "deleteMenuPointsAndLines");
}

function paintDeleteAccelerator(item: HTMLButtonElement, labelKey: MessageKey, action: ActionId): void {
  const label = item.querySelector<HTMLElement>(".delete-menu-label");
  const hint = item.querySelector<HTMLElement>(".delete-menu-accel");
  if (!label || !hint) return;
  const text = t(labelKey);
  const letter = acceleratorLetter(storedKeys.keymap, action);
  label.replaceChildren();
  const at = acceleratorIndex(text, letter);
  if (letter && at >= 0) {
    label.append(text.slice(0, at));
    const mark = document.createElement("u");
    mark.textContent = text.slice(at, at + letter.length);
    label.append(mark, text.slice(at + letter.length));
  } else {
    label.textContent = text;
  }
  const binding = storedKeys.keymap[action].find((chord) => chord.type === "key") ?? storedKeys.keymap[action][0];
  hint.textContent = binding ? formatBinding(binding) : "";
  if (binding) item.setAttribute("aria-keyshortcuts", formatBinding(binding));
}

function highlightDeleteItem(choice: DeleteMenuChoice): void {
  if (!deleteMenuOpen || deleteItem(choice).disabled) return;
  deleteHighlight = choice;
  paintDeleteMenu();
}

function stepDeleteHighlight(step: number): void {
  const choices = enabledDeleteChoices();
  if (choices.length === 0) return;
  const index = choices.indexOf(deleteHighlight);
  const from = index < 0 ? 0 : index;
  deleteHighlight = choices[(from + step + choices.length) % choices.length] ?? choices[0];
  paintDeleteMenu();
}

function cycleSelectTool(): void {
  const tool = editor.cycleSelectTool();
  if (!tool) return;
  selectToolHintUntil = Date.now() + 1600;
  window.setTimeout(() => updateHint(), 1650);
  syncChrome(false);
}

function cycleRoadsTool(): void {
  if (!roadsOpen) return;
  const tool = editor.cycleRoadsTool();
  if (!tool) return;
  selectToolHintUntil = Date.now() + 1600;
  window.setTimeout(() => updateHint(), 1650);
  syncChrome(false);
}

function openDeleteMenu(): void {
  const state = editor.deleteMenuState();
  if (!state.points && !state.lines) {
    deleteHintUntil = Date.now() + 2800;
    updateHint();
    return;
  }
  deleteHighlight = state.points ? "points" : "lines";
  deleteMenuOpen = true;
  editor.setDeleteMenuOpen(true);
  paintDeleteMenu();
  const at = editor.pointerClient();
  deleteMenu.hidden = false;
  deleteMenu.style.left = "0px";
  deleteMenu.style.top = "0px";
  const rect = deleteMenu.getBoundingClientRect();
  const left = Math.max(8, Math.min(at.x, window.innerWidth - rect.width - 8));
  const top = Math.max(8, Math.min(at.y, window.innerHeight - rect.height - 8));
  deleteMenu.style.left = `${left}px`;
  deleteMenu.style.top = `${top}px`;
}

function closeDeleteMenu(): void {
  if (!deleteMenuOpen) return;
  deleteMenuOpen = false;
  deleteMenu.hidden = true;
  editor.setDeleteMenuOpen(false);
}

function confirmDeleteChoice(choice: DeleteMenuChoice): void {
  if (!editor.deleteMenuState()[choice]) return;
  closeDeleteMenu();
  editor.confirmDeleteMenu(choice);
}

function onDeleteMenuPointer(event: PointerEvent): void {
  if (!deleteMenuOpen) return;
  const inside = event.target instanceof Node && deleteMenu.contains(event.target);
  const cancel = actionMatchesPointer(storedKeys.keymap, "cancelDeleteMenu", pointerState(event), inputPlatform);
  if (inside && !cancel) return;
  if (cancel) event.preventDefault();
  closeDeleteMenu();
}

function pointerState(e: PointerEvent): PointerState {
  return {
    kind: "click",
    button: e.button,
    alt: e.altKey,
    shift: e.shiftKey,
    ctrl: e.ctrlKey,
    meta: e.metaKey,
  };
}

function presetLabel(id: StoredKeymap["preset"]): "keymap.preset.default" {
  switch (id) {
    case "default":
      return "keymap.preset.default";
  }
}

function keyState(e: KeyboardEvent): KeyState {
  return { code: e.code, alt: e.altKey, shift: e.shiftKey, ctrl: e.ctrlKey, meta: e.metaKey };
}

function openSettings(view: "home" | "keymap" | "graphics" | "style"): void {
  settingsOpen = true;
  settingsBackdrop.hidden = false;
  editor.setInputSuspended(true);
  settingsHome.hidden = view !== "home";
  keymapPanel.hidden = view !== "keymap";
  graphicsPanel.hidden = view !== "graphics";
  styleDefaultsPanel.hidden = view !== "style";
  settingsBack.hidden = view === "home";
  settingsTitle.textContent = t(
    view === "keymap"
      ? "keymap.title"
      : view === "graphics"
        ? "graphics.title"
        : view === "style"
          ? "style.defaults"
          : "settings.title",
  );
  if (view === "keymap") renderKeymap();
  if (view === "graphics") renderGraphics();
  if (view === "style") renderStyleDefaults();
}

const GRAPHICS_DISMISS_KEY = "mapus.graphicsAccel.dismissed";
const graphicsReport: GraphicsAccelReport = readGraphicsAcceleration();

const STYLE_COLORS = FILL_SWATCHES.default.slice(PALETTE_COLUMNS, PALETTE_COLUMNS * 2);

interface ColorGroup {
  root: HTMLElement;
  color: string;
  input: HTMLInputElement;
}

function mountColorGroup(root: HTMLElement): ColorGroup {
  const input = document.createElement("input");
  input.type = "color";
  input.className = "style-color";
  const group: ColorGroup = { root, color: "#ffffff", input };
  for (const color of STYLE_COLORS) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "style-swatch";
    button.style.background = color;
    button.dataset.color = color;
    button.addEventListener("click", () => {
      group.color = color;
      paintColorGroup(group);
      group.root.dispatchEvent(new Event("change", { bubbles: true }));
    });
    root.append(button);
  }
  input.addEventListener("input", () => {
    const hex = normalizeColor(input.value);
    if (!hex) return;
    group.color = hex;
    paintColorGroup(group);
    group.root.dispatchEvent(new Event("change", { bubbles: true }));
  });
  root.append(input);
  return group;
}

function paintColorGroup(group: ColorGroup): void {
  const current = group.color.toLowerCase();
  group.input.setAttribute("aria-label", t("style.customColor"));
  group.root.querySelectorAll<HTMLButtonElement>(".style-swatch").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.color === current));
  });
  if (/^#[0-9a-f]{6}$/.test(current)) group.input.value = current;
}

function setColorGroup(group: ColorGroup, color: string): void {
  group.color = color.toLowerCase();
  paintColorGroup(group);
}

function paintPatterns(buttons: readonly HTMLButtonElement[], pattern: LinePattern): void {
  for (const button of buttons) {
    button.setAttribute("aria-pressed", String(button.dataset.pattern === pattern));
  }
}

function patternFromButtons(buttons: readonly HTMLButtonElement[]): LinePattern {
  const pressed = buttons.find((button) => button.getAttribute("aria-pressed") === "true");
  const pattern = pressed?.dataset.pattern;
  if (pattern === "dashed" || pattern === "dotted" || pattern === "solid") return pattern;
  return "solid";
}

const pointColors = mountColorGroup(stylePointSwatches);
const defaultPointColors = mountColorGroup(defaultPointSwatches);
let stylePattern: LinePattern = "solid";
let styleSeed = "";

function syncStylePanel(): void {
  const form = editor.styleForm();
  const show = form.points.length > 0 || form.lines.length > 0;
  stylePanel.hidden = !show;
  styleDock.hidden = session.mode !== "edit" || !show;
  if (!show) {
    styleSeed = "";
    return;
  }
  stylePoints.hidden = form.points.length === 0;
  styleLines.hidden = form.lines.length === 0;
  stylePointMixed.hidden = !form.pointMixed;
  styleLineMixed.hidden = !form.lineMixed;
  paintStyleFacePalette();
  const seed = [
    form.points.join(","),
    form.lines.join(","),
    form.point.color,
    form.point.size,
    form.line.color,
    form.line.width,
    form.line.pattern,
    form.pointMixed,
    form.lineMixed,
    form.decorative,
    form.decorativeMixed,
  ].join("|");
  if (seed === styleSeed) return;
  styleSeed = seed;
  setColorGroup(pointColors, form.point.color);
  lineStyleColor = form.line.color;
  stylePointSize.value = String(form.point.size);
  styleLineWidth.value = String(form.line.width);
  stylePattern = form.line.pattern;
  paintPatterns(stylePatternButtons, stylePattern);
  styleDecorativeBtn.hidden = form.lines.length === 0;
  styleDecorativeMixed.hidden = !form.decorativeMixed || form.lines.length === 0;
  styleDecorativeBtn.setAttribute("aria-pressed", String(form.decorative && !form.decorativeMixed));
  styleDecorativeBtn.disabled = form.lines.length === 0;
  paintStyleLinePaletteForce();
}

styleApply.addEventListener("click", () => {
  const form = editor.styleForm();
  const point =
    form.points.length > 0 ? normalizePointStyle({ color: pointColors.color, size: Number(stylePointSize.value) }) : null;
  const line =
    form.lines.length > 0
      ? normalizeLineStyle({ color: lineStyleColor, width: Number(styleLineWidth.value), pattern: stylePattern })
      : null;
  editor.applyStyles(point, line);
  styleSeed = "";
  syncStylePanel();
});

for (const button of stylePatternButtons) {
  button.addEventListener("click", () => {
    const pattern = button.dataset.pattern;
    if (pattern !== "solid" && pattern !== "dashed" && pattern !== "dotted") return;
    stylePattern = pattern;
    paintPatterns(stylePatternButtons, stylePattern);
  });
}

function renderStyleDefaults(): void {
  refreshPalettes();
  const defaults = readStyleDefaults();
  setColorGroup(defaultPointColors, defaults.point.color);
  defaultLineStyleColor = defaults.line.color;
  defaultPointSize.value = String(defaults.point.size);
  defaultLineWidth.value = String(defaults.line.width);
  paintPatterns(defaultPatternButtons, defaults.line.pattern);
  defaultLineGeneration = -1;
  paintDefaultLinePalette();
}

function saveStyleDefaultsFromForm(): void {
  const next = parseStyleDefaults({
    point: { color: defaultPointColors.color, size: Number(defaultPointSize.value) },
    line: {
      color: defaultLineStyleColor,
      width: Number(defaultLineWidth.value),
      pattern: patternFromButtons(defaultPatternButtons),
    },
  });
  writeStyleDefaults(next);
  editor.setStyleDefaults(next);
  renderStyleDefaults();
}

defaultPointSwatches.addEventListener("change", () => saveStyleDefaultsFromForm());
defaultPointSize.addEventListener("change", () => saveStyleDefaultsFromForm());
defaultLineWidth.addEventListener("change", () => saveStyleDefaultsFromForm());
for (const button of defaultPatternButtons) {
  button.addEventListener("click", () => {
    const pattern = button.dataset.pattern;
    if (pattern !== "solid" && pattern !== "dashed" && pattern !== "dotted") return;
    paintPatterns(defaultPatternButtons, pattern);
    saveStyleDefaultsFromForm();
  });
}

function renderGraphics(): void {
  const statusKey =
    graphicsReport.status === "on"
      ? "graphics.on"
      : graphicsReport.status === "off"
        ? "graphics.off"
        : "graphics.unknown";
  graphicsStatusValue.textContent = t(statusKey);
  graphicsStatusValue.dataset.state = graphicsReport.status;
  graphicsRendererValue.textContent = graphicsReport.renderer ?? t("graphics.rendererUnknown");
  graphicsHelp.hidden = graphicsReport.status !== "off";
}

function maybeShowGraphicsToast(): void {
  if (graphicsReport.status !== "off") return;
  try {
    if (localStorage.getItem(GRAPHICS_DISMISS_KEY) === "1") return;
  } catch {
    return;
  }
  graphicsToast.hidden = false;
}

function dismissGraphicsToast(): void {
  graphicsToast.hidden = true;
  try {
    localStorage.setItem(GRAPHICS_DISMISS_KEY, "1");
  } catch {
    /* private mode: hidden for this visit */
  }
}

maybeShowGraphicsToast();

function closeSettings(): void {
  settingsOpen = false;
  settingsBackdrop.hidden = true;
  keymapCapture = null;
  keymapIgnorePointer = null;
  editor.setInputSuspended(false);
}

function renderKeymap(): void {
  keymapPreset.textContent = t("keymap.preset", { name: t(presetLabel(storedKeys.preset)) });
  keymapList.replaceChildren();
  for (const id of ACTION_IDS) {
    const row = document.createElement("div");
    row.className = "keymap-row";
    const label = document.createElement("span");
    label.textContent = t(ACTION_LABEL[id]);
    const bindings = document.createElement("div");
    bindings.className = "keymap-bindings";
    storedKeys.keymap[id].forEach((binding, index) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "keymap-bind";
      const active = keymapCapture?.action === id && keymapCapture.index === index;
      btn.setAttribute("aria-pressed", String(active));
      btn.textContent = active ? t("keymap.capture") : formatBinding(binding);
      btn.addEventListener("pointerdown", (event) => {
        event.stopPropagation();
        keymapCapture = { action: id, index };
        keymapIgnorePointer = event.pointerId;
        keymapWarn.hidden = true;
        renderKeymap();
      });
      bindings.append(btn);
    });
    row.append(label, bindings);
    keymapList.append(row);
  }
}

function formatBinding(binding: Binding): string {
  return bindingTokens(binding, inputPlatform)
    .map((token) => {
      if (token === "alt") return t("keymap.mod.alt");
      if (token === "shift") return t("keymap.mod.shift");
      if (token === "ctrl") return t("keymap.mod.ctrl");
      if (token === "meta") return t("keymap.mod.meta");
      if (token === "drag") return t("keymap.drag");
      if (token === "click") return t("keymap.click");
      if (token === "wheel") return t("keymap.wheel");
      if (token === "middle") return t("keymap.button.middle");
      if (token === "right") return t("keymap.button.right");
      return token;
    })
    .join("+");
}

function assignBinding(action: ActionId, index: number, binding: Binding): void {
  const other = findConflict(storedKeys.keymap, action, binding, inputPlatform);
  keymapCapture = null;
  if (other) {
    keymapWarn.hidden = false;
    keymapWarn.textContent = t("keymap.conflict", { action: t(ACTION_LABEL[other]) });
    renderKeymap();
    return;
  }
  storedKeys = { preset: storedKeys.preset, keymap: replaceBinding(storedKeys.keymap, action, index, binding) };
  writeStoredKeys(storedKeys);
  editor.setKeymap(storedKeys.keymap, inputPlatform);
  keymapWarn.hidden = true;
  renderKeymap();
  paintDeleteAccelerators();
  paintCommandLabels();
}

let capturePointer: { x: number; y: number; button: number; alt: boolean; shift: boolean; ctrl: boolean; meta: boolean; moved: boolean } | null = null;

function onCaptureWheel(e: WheelEvent): void {
  if (!keymapCapture) return;
  e.preventDefault();
  e.stopPropagation();
  const slot = keymapCapture;
  assignBinding(
    slot.action,
    slot.index,
    bindingFromWheel(
      { alt: e.altKey, shift: e.shiftKey, ctrl: e.ctrlKey, meta: e.metaKey },
      inputPlatform,
    ),
  );
}

function onCapturePointerDown(e: PointerEvent): void {
  if (
    fillPaletteOpen &&
    !(e.target instanceof Node && (fillPalette.contains(e.target) || e.target === fillHintChip))
  ) {
    closeFillPalette();
  }
  if (!keymapCapture || e.pointerId === keymapIgnorePointer) return;
  if (e.target instanceof Element && e.target.closest("button, a, input, label")) return;
  if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;
  e.preventDefault();
  e.stopPropagation();
  capturePointer = {
    x: e.clientX,
    y: e.clientY,
    button: e.button,
    alt: e.altKey,
    shift: e.shiftKey,
    ctrl: e.ctrlKey,
    meta: e.metaKey,
    moved: false,
  };
}

function onCapturePointerMove(e: PointerEvent): void {
  if (!capturePointer) return;
  if (Math.hypot(e.clientX - capturePointer.x, e.clientY - capturePointer.y) > 4) capturePointer.moved = true;
}

function onCapturePointerUp(e: PointerEvent): void {
  if (e.pointerId === keymapIgnorePointer) {
    keymapIgnorePointer = null;
    return;
  }
  if (!keymapCapture || !capturePointer) return;
  e.preventDefault();
  e.stopPropagation();
  const pointer = capturePointer;
  capturePointer = null;
  const slot = keymapCapture;
  assignBinding(
    slot.action,
    slot.index,
    bindingFromPointer(
      {
        kind: pointer.moved ? "drag" : "click",
        button: pointer.button,
        alt: pointer.alt,
        shift: pointer.shift,
        ctrl: pointer.ctrl,
        meta: pointer.meta,
      },
      inputPlatform,
    ),
  );
}

function readStoredKeys(): StoredKeymap {
  try {
    return loadKeymap(localStorage);
  } catch {
    return { preset: "default", keymap: loadKeymap(emptyStore()).keymap };
  }
}

function writeStoredKeys(value: StoredKeymap): void {
  try {
    saveKeymap(localStorage, value);
  } catch {
    /* private mode */
  }
}

function resetStoredKeys(): StoredKeymap {
  try {
    return resetKeymap(localStorage);
  } catch {
    return { preset: "default", keymap: loadKeymap(emptyStore()).keymap };
  }
}

function emptyStore() {
  return { getItem: () => null, setItem: () => {}, removeItem: () => {} };
}

function initStreetViewFromConfig(config: {
  naver_map_key_id: string | null;
  kakao_map_js_key: string | null;
}): void {
  const keys = normalizeStreetViewKeys(config);
  setStreetViewKeys(keys);
  applyStreetViewKeysToMaps(keys);
}

syncAdPreviewFromLocation();
syncChrome(false);
const authReady = bootAuth();

async function bootAuth(): Promise<void> {
  try {
    const config = await getConfig();
    googleClientId = config.google_client_id;
    setMaptilerKey(config.maptiler_key);
    setVworldKey(config.vworld_key);
    initStreetViewFromConfig(config);
    initAdsFromPublicConfig(config);
    privacyLink.href = locale() === "ko" ? "/privacy/ko" : "/privacy/en";
    refreshLegalFooterLinks();
  } catch (err) {
    reportError(err, { location: "bootAuth:getConfig" });
    googleClientId = null;
  }
  try {
    me = await getMe();
  } catch (err) {
    reportError(err, { location: "bootAuth:getMe" });
    me = null;
  }
  syncAccount();
  renderGoogleButton();
  // Ownership may have changed what the open piece allows (Edit, Publish, Delete).
  if (editor.map.loaded()) applySession();
  void refreshQuestList();
  void refreshSavedList();
  if (nearby.length === 0 && editor.map.loaded()) syncNearbyList();
  if (activeQuestRoute) void bootQuestPage(activeQuestRoute);
}

function renderGoogleButton(): void {
  googleBtn.replaceChildren();
  signinPromptGoogle.replaceChildren();
  if (me || !googleClientId) return;
  const paint = (host: HTMLElement) => {
    window.google?.accounts.id.renderButton(host, {
      theme: "outline",
      size: "medium",
      shape: "pill",
      text: "signin_with",
    });
  };
  const render = () => {
    window.google?.accounts.id.initialize({
      client_id: googleClientId as string,
      callback: (response) => {
        void onGoogleCredential(response.credential);
      },
    });
    paint(googleBtn);
    if (signinPromptOpen) paint(signinPromptGoogle);
    const routeSignin = questRoot.querySelector<HTMLElement>("#route-signin-google");
    if (routeSignin) paint(routeSignin);
  };
  if (window.google?.accounts.id) {
    render();
    return;
  }
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (window.google?.accounts.id) {
      window.clearInterval(timer);
      render();
    } else if (tries > 20) {
      window.clearInterval(timer);
    }
  }, 250);
}

async function onGoogleCredential(idToken: string): Promise<void> {
  try {
    me = await signInWithGoogle(idToken);
    if (questRouteId && !questRouteOpen) {
      location.reload();
      return;
    }
    syncAccount();
    renderGoogleButton();
    showToast(t("account.hello", { name: me.name ?? t("account.runner") }));
    if (pendingSaveAfterSignIn) {
      pendingSaveAfterSignIn = false;
      signinPromptOpen = false;
      applySession();
      await onSave({ stayInEdit: leaveStayInEdit() });
    } else {
      signinPromptOpen = false;
      applySession();
    }
    void refreshQuestList();
    void refreshSavedList();
    if (nearby.length === 0) syncNearbyList();
    if (loadedQuest) void loadById(loadedQuest.id, { quiet: true, listOpenGen: homeListCardGen });
    if (activeQuestRoute && !questPage) void bootQuestPage(activeQuestRoute);
  } catch (err) {
    showToast(shownError(err, "account.signInFailed"));
  }
}

async function onLogout(): Promise<void> {
  const accountPiece = activeUserPieceId != null && (activeUserPieceOnServer || activeUserPieceOwner != null);
  // A saved piece cannot be saved once signed out, so unsaved edits would be lost.
  if (accountPiece && session.mode === "edit" && session.dirty && !window.confirm(t("account.logoutDiscard"))) return;
  try {
    await signOut();
  } catch {
    /* still clear locally */
  }
  me = null;
  pendingSaveAfterSignIn = false;
  signinPromptOpen = false;
  if (accountPiece) {
    // Close the account's piece: no Edit, Publish, or Delete, and nothing left behind in the draft.
    activeUserPieceId = null;
    activeUserPiecePublished = false;
    activeUserPieceOnServer = false;
    activeUserPieceOwner = null;
    cancelDraftSave();
    clearDraft();
    if (loadedQuest == null) {
      titleInput.value = "";
      editor.clear();
      editor.resetPieceStyleDefaults();
      rememberSaved("", editor.getGraph());
      stripId();
    }
    session = openPieceSession();
  }
  syncAccount();
  renderGoogleButton();
  applySession();
  showToast(t("account.signedOut"));
  void refreshQuestList();
  void refreshSavedList();
  if (nearby.length === 0) syncNearbyList();
}

function syncAccount(): void {
  logoutBtn.hidden = !me;
  if (me) {
    meChip.hidden = false;
    meName.textContent = me.name ?? t("account.runner");
    meTrust.textContent = t("account.trust", { trust: me.trust });
  } else {
    meChip.hidden = true;
  }
}

async function onSave(opts: { stayInEdit: boolean }): Promise<boolean> {
  if (!editingActive(session) && !session.leavePrompt) return false;
  if (questRouteId && questRouteLiveEdit && !confirmAdminPublishedSave(t("rt.adminPublished.banner"))) return false;
  if (questRouteId) titleInput.value = questRouteTitle || t("qroute.defaultName");
  const title = titleInput.value.trim();
  if (!title) {
    titleInput.focus();
    showToast(t("toast.needTitle"));
    return false;
  }
  if (!me) {
    pendingSaveAfterSignIn = true;
    signinPromptOpen = true;
    applySession();
    renderGoogleButton();
    return false;
  }
  if (questRouteId) {
    const route = editor.getRoute();
    if (!route.ok) {
      showToast(t(route.reason === "disconnected" ? "route.disconnected" : "route.empty"));
      return false;
    }
  }
  const id = activeUserPieceId ?? crypto.randomUUID();
  const ownerId = me.id;
  const data: UserPieceData = { graph: editor.getGraph() };
  saveBtn.disabled = true;
  saveBtn.textContent = t("action.saving");
  try {
    const saved = await saveUserPiece(id, { name: title, data });
    activeUserPieceId = saved.id;
    activeUserPiecePublished = saved.published === true;
    activeUserPieceOnServer = true;
    activeUserPieceOwner = ownerId;
    loadedQuest = null;
    editor.setQuestView(null, 0);
    rememberSaved(saved.name, saved.data.graph);
    await pieceStore.putUserPiece({
      id: saved.id,
      name: saved.name,
      data: saved.data,
      updatedAt: saved.updated_at,
      savedToServer: true,
      ownerId,
    });
    cancelDraftSave();
    clearDraft();
    history.replaceState(null, "", pieceUrl(saved.id));
    if (questRouteId) writeQuestRoutePieceId(questRouteId, saved.id);
    if (questRouteId) {
      const bound = await sendRouteToQuest(questRouteId, saved.id);
      if (!bound) {
        session = markSessionDirty(session);
        applySession();
        syncChrome(false);
        return false;
      }
      clearQuestRoutePieceId(questRouteId);
    }
    session = markSessionSaved(session);
    if (!opts.stayInEdit) session = openPieceSession();
    applySession();
    if (!questRouteId) showToast(t("toast.saved"));
    syncChrome(false);
    return true;
  } catch (err) {
    showToast(shownError(err, "toast.saveFailed"));
    return false;
  } finally {
    saveBtn.disabled = !editingActive(session);
    saveBtn.textContent = labelWithShortcut("savePiece", t("action.save"));
  }
}

async function onGpx(file: File): Promise<void> {
  try {
    const gpx = parseGpx(await file.text());
    const points = simplifyToMax(gpx.points, 8, 2000);
    if (!titleInput.value.trim() && gpx.name) titleInput.value = gpx.name.slice(0, 80);
    editor.importLine(points);
    showToast(gpx.source === "route" ? t("toast.gpxRoute") : t("toast.gpxTrack"));
  } catch (err) {
    showToast(shownError(err, "gpx.failed"));
  }
}

function homeSpotPinsFromNearby(item: NearbyQuest): HomeSpotPin[] | null {
  if ((item.route_preview?.coordinates?.length ?? 0) >= 2) return null;
  const spots = item.spots_preview;
  if (!spots?.length) return null;
  return spots.map((s) => ({
    lat: s.lat,
    lng: s.lng,
    kind: s.kind,
    title: s.title ?? null,
    thumbnail_image: s.thumbnail_image ?? null,
  }));
}

function homeSpotPinsFromDetail(quest: QuestDetail): HomeSpotPin[] | null {
  if (quest.route && quest.route.coordinates.length >= 2) return null;
  if (!quest.checkpoints?.length) return null;
  return quest.checkpoints.map((s) => ({
    lat: s.lat,
    lng: s.lng,
    kind: s.kind,
    title: s.title,
    thumbnail_image: s.thumbnail_image ?? null,
  }));
}

/** Open a published quest in the detail panel and draw its route, like a public Piece used to open. */
async function loadById(
  id: string,
  opts: {
    fit?: boolean;
    quiet?: boolean;
    panToStart?: boolean;
    listOpenGen?: number;
    holdCamera?: boolean;
  } = {},
): Promise<void> {
  if (questRouteId) return;
  const gen = opts.listOpenGen ?? ++homeListCardGen;
  const listOpenLive = (): boolean => gen === homeListCardGen;
  if (!opts.holdCamera) {
    listPreviewQuest = null;
    listPreview.hidePreviewChrome();
  }
  if (!opts.quiet) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.loading");
  }
  const usableHomeQuest = (q: QuestDetail | null): q is QuestDetail =>
    !!q && homeListed(q) && ((q.route?.coordinates.length ?? 0) >= 2 || q.checkpoints.length >= 1);
  const freshPromise = getQuest(id).then(
    (q) => ({ ok: true as const, q }),
    (err: unknown) => ({ ok: false as const, err }),
  );
  const cachedPromise = cachedQuest(id);
  const raced = await Promise.race([
    cachedPromise.then((q) => ({ from: "cache" as const, q })),
    freshPromise.then((res) => ({ from: "fresh" as const, res })),
  ]);
  if (!listOpenLive()) return;
  if (raced.from === "cache" && usableHomeQuest(raced.q)) {
    const cached = raced.q;
    showLoadedQuest(cached, opts);
    const fresh = await freshPromise;
    if (!listOpenLive()) return;
    if (fresh.ok && loadedQuest?.id === id && JSON.stringify(fresh.q) !== JSON.stringify(cached)) {
      showLoadedQuest(fresh.q, { quiet: true, holdCamera: editor.questViewIsFramed() });
    } else if (!fresh.ok && fresh.err instanceof ApiError && fresh.err.status === 404 && loadedQuest?.id === id) {
      closeLoadedQuest();
      showToast(t("home.notFound"));
      void refreshNearby();
    }
    return;
  }
  const fresh = raced.from === "fresh" ? raced.res : await freshPromise;
  if (!listOpenLive()) return;
  if (!fresh.ok) {
    if (opts.quiet) return;
    const err = fresh.err;
    showToast(err instanceof ApiError && err.status === 404 ? t("home.notFound") : shownError(err, "toast.loadFailed"));
    syncChrome(false);
    return;
  }
  showLoadedQuest(fresh.q, opts);
}

/**
 * Open one of your drafts like a published quest. Drafts are read only from the signed-in
 * missions API (no client or edge cache) and never go into the page URL.
 */
async function openOwnDraft(
  id: string,
  opts: {
    fit?: boolean;
    listOpenGen?: number;
    quiet?: boolean;
    panToStart?: boolean;
    holdCamera?: boolean;
  } = {},
): Promise<void> {
  if (questRouteId || !me) return;
  const gen = opts.listOpenGen ?? ++homeListCardGen;
  const listOpenLive = (): boolean => gen === homeListCardGen;
  if (!opts.holdCamera) {
    listPreviewQuest = null;
    listPreview.hidePreviewChrome();
  }
  hintEl.hidden = false;
  hintEl.textContent = t("hint.loading");
  try {
    const quest = await getMission(id);
    if (!listOpenLive()) return;
    showLoadedQuest(quest, {
      fit: opts.fit === true,
      quiet: opts.quiet,
      panToStart: opts.panToStart,
      holdCamera: opts.holdCamera,
      ownerApi: true,
    });
  } catch (err) {
    if (!listOpenLive()) return;
    showToast(err instanceof ApiError && err.status === 404 ? t("home.notFound") : shownError(err, "toast.loadFailed"));
    syncChrome(false);
  }
}

/** Put a quest in the detail panel and its route on the map (an own quest may have no route yet). */
function showLoadedQuest(
  quest: QuestDetail,
  opts: {
    fit?: boolean;
    quiet?: boolean;
    ownerApi?: boolean;
    panToStart?: boolean;
    holdCamera?: boolean;
  } = {},
): void {
  if (!detailOpenable(quest, { ownerApi: opts.ownerApi === true })) throw new ApiError(t("home.notFound"), 404);
  const route = quest.route && quest.route.coordinates.length >= 2 ? quest.route : null;
  const spotPins = homeSpotPinsFromDetail(quest);
  const previousId = loadedQuest?.id ?? null;
  const reopened = previousId === quest.id;
  if (previousId != null && !reopened) editor.clearQuestFocus();
  loadedQuest = quest;
  if (typeof opts.fit === "boolean" && !opts.holdCamera) editor.setQuestViewFramed(opts.fit);
  activeUserPieceId = null;
  activeUserPiecePublished = false;
  activeUserPieceOnServer = false;
  activeUserPieceOwner = null;
  titleInput.value = quest.title;
  const viewChanged =   editor.setQuestView(
    route?.coordinates ?? null,
    routeCanvasBearing(route?.canvas),
    route?.paint,
    route?.fill,
    spotPins,
    route?.canvas ?? null,
    opts.holdCamera === true,
  );
  const applyGraph = (): void => {
    if (loadedQuest?.id !== quest.id) return;
    if (route) {
      const graph = graphFromQuestRoute(route.coordinates, route.closed, route.canvas, route.paint);
      editor.loadGraph(graph);
      rememberSaved(quest.title, graph);
    } else {
      editor.clear();
      rememberSaved(quest.title, editor.getGraph());
    }
  };
  const paintLoaded = (): void => {
    if (loadedQuest?.id !== quest.id) return;
    // Drop the list summary before the session paints, or syncInlinePage(null) tears down
    // the quest-page column the camera already fitted.
    listPreviewQuest = null;
    listPreview.hidePreviewChrome();
    if (!reopened || !opts.quiet) applyGraph();
    session = openPieceSession();
    applySession();
    syncNearbyList();
    syncSavedList();
    syncHomeListsCurrent();
  };
  // Graph build, the inline quest map, and the list repaint all run after the ease has
  // painted. Doing them in the click turn or in a same-frame rAF holds the first pixels back.
  if (opts.holdCamera) {
    editor.flushDeferredQuestLayers();
    afterHomeQuestEase(paintLoaded);
  } else {
    paintLoaded();
  }
  const shouldFitNow = (opts.fit || Boolean(spotPins)) && Boolean(route || spotPins) && !opts.holdCamera;
  if (shouldFitNow) {
    void mapEl.offsetWidth;
    void sheetEl.offsetWidth;
    void pieceDetail.offsetWidth;
    editor.showQuestStart();
  } else if (!opts.holdCamera && (!opts.quiet || !reopened || opts.panToStart)) {
    const pan = opts.panToStart ?? !opts.quiet;
    const { target: view } = compassStart(route ? routeCanvasBearing(route?.canvas) : 0);
    editor.focusQuestStart(quest.id, questMapPoint(quest), {
      pan,
      delayMs: pan ? LIST_REVEAL_DELAY_MS : undefined,
      view: pan ? view : undefined,
    });
  }
  // Quiet refresh: re-fit only when this quest was opened with zoom-to-route.
  if (
    (route || spotPins) &&
    !opts.fit &&
    !opts.holdCamera &&
    reopened &&
    opts.quiet &&
    viewChanged &&
    editor.questViewIsFramed()
  ) {
    editor.showQuestStart();
  }
  if (quest.status === "draft") stripId();
  else history.replaceState(null, "", `/?id=${encodeURIComponent(quest.id)}`);
  if (!opts.holdCamera) {
    listPreviewQuest = null;
    listPreview.hidePreviewChrome();
  }
  if (route && opts.ownerApi !== true) void rememberPublicQuest(quest);
  if (opts.quiet) return;
  showToast(route || spotPins ? t("toast.loaded", { title: questTitle(quest) }) : t("quests.noRoute"));
}

function selectedQuestId(): string | null {
  return loadedQuest?.id ?? listPreviewQuest?.id ?? null;
}

function findHomeListQuest(id: string): NearbyQuest | null {
  return (
    nearby.find((q) => q.id === id) ??
    savedQuests.find((q) => q.id === id) ??
    myListedQuests.find((q) => q.id === id) ??
    null
  );
}

/** Clear a home list or loaded quest selection without moving the map. */
function clearHomeQuestSelection(): void {
  if (!selectedQuestId()) return;
  homeListCardGen += 1;
  closeLoadedQuest();
}

function clearQuestMapForSelection(): void {
  // Drop the route bookkeeping without rebuilding layers. The graph wipe waits until the
  // next ease has painted; doing it here blocks the first camera frame.
  editor.setQuestView(null, 0, null, null, null, null, true);
  editor.setQuestViewFramed(false);
  titleInput.value = "";
  loadedQuest = null;
  syncInlinePage(null);
  const wipe = (): void => {
    editor.clear();
    editor.resetPieceStyleDefaults();
    rememberSaved("", editor.getGraph());
  };
  const map = editor.map;
  let eased = false;
  const onStart = (): void => {
    eased = true;
    map.off("movestart", onStart);
    map.once("moveend", () => requestAnimationFrame(wipe));
  };
  if (map.isEasing() || map.isMoving()) onStart();
  else {
    map.once("movestart", onStart);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (eased) return;
      map.off("movestart", onStart);
      wipe();
    }));
  }
}

/** After the quest ease has painted. setTimeout yields the arrival frame; a rAF would not. */
function afterHomeQuestEase(run: () => void): void {
  const map = editor.map;
  const fire = (): void => {
    setTimeout(run, 0);
  };
  if (map.isEasing() || map.isMoving()) map.once("moveend", fire);
  else fire();
}

let spotTitleProbe: HTMLSpanElement | null = null;

/** Used width of a home-map spot title, including the 160px ellipsis cap. */
function measureSpotTitlePx(title: string): number {
  const text = title.trim();
  if (!text) return 0;
  if (!spotTitleProbe) {
    spotTitleProbe = document.createElement("span");
    spotTitleProbe.className = "quest-spot-dom-marker-title";
    spotTitleProbe.style.left = "-10000px";
    spotTitleProbe.style.top = "0";
    document.body.append(spotTitleProbe);
  }
  spotTitleProbe.textContent = text;
  return spotTitleProbe.offsetWidth;
}

/**
 * A route page and a spot page both become the 380px right column. Mark that before the
 * fit so the camera reads the panel rect, not the short preview card.
 */
function homeListQuestPageColumn(item: NearbyQuest): boolean {
  if ((questListRouteCoordinates(item)?.length ?? 0) >= 2) return true;
  return isSpotOnlyListQuest(item);
}

/**
 * A route page, and a published spot page (map + ad + cards), fill the right column.
 * A draft spot page stays a short top-right card, so it must not take a side inset.
 */
function homeListQuestFillsColumn(item: NearbyQuest): boolean {
  if ((questListRouteCoordinates(item)?.length ?? 0) >= 2) return true;
  return isSpotOnlyListQuest(item) && item.status !== "draft";
}

/** List card: frame from the preview geometry on the click, before the detail fetch. */
function showListQuestPreview(item: NearbyQuest): void {
  // Quest-route embed (`/?route=…`) always full-loads; home list cards are not used there.
  if (questRouteId) return;
  const wipeEditor = loadedQuest != null || editor.pointCount() > 0;
  // Keep the previous graph and inline map until the ease has painted. Destroying either
  // one here is the multi-second click.
  loadedQuest = null;
  listPreviewQuest = item;
  activeUserPieceId = null;
  activeUserPiecePublished = false;
  activeUserPieceOnServer = false;
  activeUserPieceOwner = null;
  editor.setQuestViewFramed(false);
  const spotPins = homeSpotPinsFromNearby(item);
  const line = questListRouteCoordinates(item);
  if (homeListQuestPageColumn(item)) {
    // The 380px rule needs data-mode=view, and a hidden body forces display:none on the
    // whole panel (`:has(> .piece-detail-body[hidden])`), so the fit would see no right inset.
    appEl.dataset.mode = "view";
    pieceDetail.hidden = false;
    pieceDetailBody.hidden = false;
    pieceDetail.classList.add("is-open", "has-quest-page");
    detailOpen = true;
    // Published pages grow past half the screen. Reserve that height now so the fit
    // reads the column the page will occupy, not the empty shell.
    pieceDetail.style.minHeight = homeListQuestFillsColumn(item) ? "52dvh" : "";
  }
  if (spotPins) {
    for (const pin of spotPins) {
      const title = pin.title?.trim();
      if (title) rememberHomeSpotTitleWidth(title, measureSpotTitlePx(title));
    }
  }
  editor.setQuestView(
    line,
    routeCanvasBearing(item.route_preview?.canvas),
    item.route_preview?.paint ?? null,
    item.route_preview?.fill ?? null,
    spotPins,
    item.route_preview?.canvas ?? null,
    true,
  );
  stripId();
  syncMapControlChrome();
  if ((line?.length ?? 0) >= 2 || spotPins) {
    void mapEl.offsetWidth;
    void sheetEl.offsetWidth;
    void pieceDetail.offsetWidth;
    editor.showQuestStart();
  } else if (wipeEditor) {
    clearQuestMapForSelection();
  }
  syncHomeListsCurrent();
  const gen = homeListCardGen;
  afterHomeQuestEase(() => {
    if (gen !== homeListCardGen) return;
    // The loaded quest paints its own page. Running the summary here calls
    // syncInlinePage(null) and drops has-quest-page before that page mounts.
    if (loadedQuest != null) return;
    if (wipeEditor) {
      editor.clear();
      editor.resetPieceStyleDefaults();
      rememberSaved("", editor.getGraph());
    }
    session = openPieceSession();
    applySession();
  });
}

function paintListQuestPreview(item: NearbyQuest): void {
  revealDetail(`q:${item.id}`);
  listPreview.paint(item, detailSaveBusy, nearbyViewer.at());
  // A list click already reserved the quest-page column for the fit. Clearing the inline
  // page would remove has-quest-page and the camera's right inset.
  if (!pieceDetail.classList.contains("has-quest-page")) syncInlinePage(null);
}

function paintDetailSaveHeart(quest: { status: string; saved?: boolean }): void {
  listPreview.paintSaveHeart(quest, detailSaveBusy);
}

function patchQuestSavedInLists(id: string, saved: boolean): void {
  const patch = (q: NearbyQuest): NearbyQuest => (q.id === id ? { ...q, saved } : q);
  nearby = nearby.map(patch);
  savedQuests = savedQuests.map(patch);
  myListedQuests = myListedQuests.map(patch);
  if (listPreviewQuest?.id === id) listPreviewQuest = { ...listPreviewQuest, saved };
}

let nearbyTimer: ReturnType<typeof setTimeout> | null = null;
let nearbyGen = 0;
let nearbyPinsGen = 0;
/** A list card waits this long so the list and panel repaint before the camera eases. */
const LIST_REVEAL_DELAY_MS = 400;

function isNearbyTabActive(): boolean {
  return pieceTabs.some((b) => b.dataset.tab === "nearby" && b.getAttribute("aria-selected") === "true");
}

function scheduleNearbyMapSync(): void {
  if (nearbyTimer != null) return;
  nearbyTimer = setTimeout(() => {
    nearbyTimer = null;
    void refreshNearbyPins();
    paintNearbySearchAreaButton();
  }, 200);
}

function paintNearbySearchAreaButton(): void {
  const stale =
    !questRouteId &&
    isNearbyTabActive() &&
    nearbyListAnchor != null &&
    nearbySearchAreaStaleForMap(editor.map, nearbyListAnchor);
  const show = nearbySearchAreaLoading || stale;
  nearbySearchAreaBtn.hidden = !show;
  nearbySearchAreaBtn.disabled = nearbySearchAreaLoading;
  nearbySearchAreaBtn.classList.toggle("is-loading", nearbySearchAreaLoading);
  nearbySearchAreaBtn.setAttribute("aria-busy", nearbySearchAreaLoading ? "true" : "false");
}

function nearbyPins(quests: readonly NearbyQuest[]): Array<{ id: string; title: string; at: [number, number] }> {
  const pins: Array<{ id: string; title: string; at: [number, number] }> = [];
  for (const q of quests) {
    const at = questMapPoint(q);
    if (at) pins.push({ id: q.id, title: q.title, at });
  }
  return pins;
}

async function refreshNearbyPins(): Promise<void> {
  if (questRouteId) return;
  const gen = ++nearbyPinsGen;
  try {
    const viewer = nearbyViewer.at();
    const res = await listNearbyQuests(editor.viewportBBox(), viewer);
    if (gen !== nearbyPinsGen) return;
    editor.setNearby(nearbyPins(res.quests.filter(homeListed)));
  } catch {
    /* pins are best-effort while the list stays frozen */
  }
}

async function refreshNearby(opts: { scrollTop?: boolean } = {}): Promise<void> {
  if (questRouteId) return;
  const gen = ++nearbyGen;
  ++nearbyPinsGen;
  try {
    const viewer = nearbyViewer.at();
    const res = await listNearbyQuests(editor.viewportBBox(), viewer);
    if (gen !== nearbyGen) return;
    nearby = res.quests.filter(homeListed);
    if (viewer) {
      nearbyListAnchor = { lng: viewer.lng, lat: viewer.lat, zoom: editor.map.getZoom() };
    }
    editor.setNearby(nearbyPins(nearby));
    syncNearbyList();
    syncHomeListsCurrent();
    nearbySearchAreaBtn.hidden = true;
    if (opts.scrollTop) nearbyList.scrollTop = 0;
  } catch (err) {
    if (gen !== nearbyGen) return;
    nearbyList.innerHTML = `<p class="empty">${escapeHtml(shownError(err, "nearby.failed"))}</p>`;
  }
}

function syncChrome(edited: boolean): void {
  if (edited) markDirty();
  const route = routeForChrome();
  const n = editor.pointCount();
  pointCountEl.textContent = String(n);
  pointLabelEl.textContent = t("stats.points", { count: n });
  const parts = n > 0 ? editor.partCount() : 0;
  const cut = parts > 1;
  if (cut) {
    distanceEl.textContent = t("stats.disconnected", { count: formatCount(parts) });
    repeatEl.textContent = "—";
    etaEl.textContent = "—";
  } else if (route != null && route.ok) {
    distanceEl.textContent = formatDistance(route.routeLengthM);
    repeatEl.textContent = formatDistance(route.repeatedLengthM);
    etaEl.textContent = formatEta(route.routeLengthM);
  } else {
    distanceEl.textContent = "—";
    repeatEl.textContent = "—";
    etaEl.textContent = "—";
  }
  distanceEl.classList.toggle("stats-warn", cut);
  if (cut) {
    distanceEl.setAttribute("role", "button");
    distanceEl.tabIndex = 0;
    distanceEl.title = t("stats.disconnectedNext");
  } else {
    distanceEl.removeAttribute("role");
    distanceEl.removeAttribute("tabindex");
    distanceEl.removeAttribute("title");
    issueCursor.part = 0;
  }
  syncIssueChips();
  selectPartsBtn.hidden = !cut;
  selectPartsBtn.textContent = t("edit.selectSmallerParts");
  undoBtn.disabled = !editor.canUndo();
  redoBtn.disabled = !editor.canRedo();
  clearBtn.disabled = n === 0;
  saveBtn.disabled = !editingActive(session);
  const hasSel = editor.hasSelection();
  deletePointBtn.disabled = !hasSel;
  setStartBtn.disabled = editor.selectedIndex() == null;
  moveBtn.disabled = !hasSel;
  joinBtn.disabled = !hasSel;
  mergeBtn.disabled = !hasSel;
  deleteMenuBtn.disabled = !hasSel;
  deleteSelectedBtn.disabled = !hasSel;
  deselectBtn.disabled = !hasSel;
  copyStyleBtn.disabled = !hasSel;
  pasteStyleBtn.disabled = !hasSel;
  const styleForm = editor.styleForm();
  const hasLines = styleForm.lines.length > 0;
  toggleDecorativeBtn.disabled = false;
  toggleDecorativeBtn.setAttribute(
    "aria-pressed",
    String(
      hasLines ? styleForm.decorative && !styleForm.decorativeMixed : editor.decorativeStrokeEnabled(),
    ),
  );
  paintSelectTool();
  paintRoadsTool();
  syncStylePanel();

  magnetBtn.setAttribute("aria-pressed", String(editor.magnetEnabled()));
  const pointsOn = editor.pointsVisible();
  pointsToggleBtn.setAttribute("aria-pressed", String(pointsOn));
  roadsBtn.setAttribute("aria-pressed", String(roadsOpen));
  roadsRow.hidden = !roadsOpen;
  spacingRow.hidden = !roadsOpen;
  const eraseOn = editor.interactionMode() === "erase";
  eraseBtn.setAttribute("aria-pressed", String(eraseOn));
  eraseSizeRow.hidden = !eraseOn;
  if (eraseOn) {
    const wheel = storedKeys.keymap.resizeEraseBrush.find((chord) => chord.type !== "key");
    const sizeText = t("eraseSize.label");
    eraseSizeLabel.textContent = wheel ? `${sizeText} (${formatBinding(wheel)})` : sizeText;
    const radius = String(Math.round(editor.eraseBrushRadius()));
    if (eraseSizeInput.value !== radius) eraseSizeInput.value = radius;
    eraseSizeValue.textContent = radius;
  }
  workAreaBtn.setAttribute("aria-pressed", String(editor.interactionMode() === "work"));
  workAreaClearBtn.disabled = !editor.hasWorkArea();
  boundaryDoneBtn.hidden = !editor.canCloseBoundary();
  boundaryClearBtn.disabled = !editor.hasBoundary();
  const spacingLabel = String(editor.spacingMeters());
  if (spacingInput.value !== spacingLabel) spacingInput.value = spacingLabel;
  spacingValue.textContent = t("spacing.meters", { meters: spacingLabel });
  paintRanges();
  paintCommandLabels();

  loopBadge.hidden = !editor.isClosed();

  updateHint();
  paintPieceDetail();
}

function paintPieceDetail(): void {
  paintPieceDetailBody();
  questDetailClose.hidden = selectedQuestId() == null;
  detailMenuWrap.hidden = [geojsonLink, gpxLink, publicPieceDelete].every((el) => el.hidden);
  if (detailMenuWrap.hidden) detailMenu.close();
  syncMapControlChrome();
}

/** The Clear cache menu item: drop cached quests, nearby lists and old Piece copies, then load again. */
async function clearCachedData(): Promise<void> {
  sheetMenu.close();
  try {
    await clearQuestCache();
  } catch (err) {
    showToast(shownError(err, "toast.loadFailed"));
    return;
  }
  showToast(t("cache.cleared"));
  void refreshNearby();
  if (loadedQuest) void loadById(loadedQuest.id, { quiet: true, listOpenGen: homeListCardGen });
  else if (listPreviewQuest) {
    const item = findHomeListQuest(listPreviewQuest.id);
    if (item) showListQuestPreview(item);
  }
}

function paintPieceDetailBody(): void {
  if (session.mode !== "view") {
    pieceDetailBody.hidden = true;
    syncInlinePage(null);
    return;
  }
  if (listPreviewQuest) {
    paintListQuestPreview(listPreviewQuest);
    return;
  }
  if (loadedQuest) {
    paintQuestDetail(loadedQuest);
    return;
  }
  listPreview.hidePreviewChrome();
  syncInlinePage(null);
  pieceDetailBadge.hidden = true;
  questDetailEdit.hidden = true;
  questDetailEdit.removeAttribute("href");
  pieceDetailStrip.hidden = true;
  geojsonLink.removeAttribute("href");
  gpxLink.removeAttribute("href");
  geojsonLink.hidden = true;
  gpxLink.hidden = true;
  const route = routeForChrome();
  const n = editor.pointCount();
  // An own unpublished piece shows even when empty, so its Edit button stays reachable.
  const hasPiece = activeUserPieceId != null && (n > 0 || canEnterEdit());
  if (!hasPiece) {
    pieceDetailBody.hidden = true;
    detailPieceKey = null;
    pieceDetailStatus.hidden = true;
    pieceDetailPublish.hidden = true;
    pieceDetailEdit.hidden = true;
    publicPieceDelete.hidden = true;
    return;
  }
  revealDetail(`p:${activeUserPieceId}`);
  pieceDetailTitle.textContent = titleInput.value.trim() || t("saved.new");
  pieceDetailPointsLabel.textContent = t("stats.points", { count: n });
  pieceDetailPoints.textContent = String(n);
  pieceDetailDistance.textContent = route != null && route.ok ? formatDistance(route.routeLengthM) : "—";
  pieceDetailMeta.textContent = me ? me.name ?? t("account.runner") : "";

  const ownPrivate = canEnterEdit();
  pieceDetailStatus.hidden = !ownPrivate;
  if (ownPrivate) pieceDetailStatus.textContent = t("status.private");
  pieceDetailPublish.hidden = true;
  pieceDetailEdit.hidden = !ownPrivate;
  if (ownPrivate) {
    pieceDetailEdit.textContent = labelWithShortcut("enterEdit", t("mode.edit"));
  }
  // The server keeps published drafts locked, so only unpublished ones delete here.
  publicPieceDelete.hidden = activeUserPiecePublished || !ownPieceDeletable(activePieceAccess());
}

function questInlineSyncKey(quest: QuestDetail): string {
  const { saved: _omit, ...rest } = quest;
  return JSON.stringify(rest);
}

function syncInlinePage(quest: QuestDetail | null): void {
  const show = quest != null && usesQuestPageTemplate(quest);
  const key = show ? questInlineSyncKey(quest) : "";
  if (inlinePage?.key === key) return;
  inlinePage?.view.destroy();
  inlinePage = null;
  questInlinePage.replaceChildren();
  questInlinePage.hidden = !show;
  pieceDetail.classList.toggle("has-quest-page", show);
  if (!show) {
    pieceDetail.style.minHeight = "";
    return;
  }
  const view = new RouteTraceEditor({
    root: questInlinePage,
    quest,
    mode: "view",
    layout: "inline",
    onToast: showToast,
    signedIn: () => me != null,
    onRequireSignIn: promptSignInToSave,
    onSavedChange: (saved) => {
      if (loadedQuest?.id !== quest.id) return;
      loadedQuest = { ...loadedQuest, saved };
      paintDetailSaveHeart(loadedQuest);
      void refreshSavedList();
    },
    onBeforeOpenRouteEditor: () => {
      persistHomeBrowseReturn({ returnToHome: true, questId: quest.id });
    },
    onSpotMapSelectionChange: (index) => editor.setHomeQuestSpotSelection(index),
  });
  view.mount();
  inlinePage = { key, view };
  requestAnimationFrame(() => {
    requestAnimationFrame(() => inlinePage?.view.relayout());
  });
}

/** Reopen the detail drawer when a different quest or piece is shown. */
function revealDetail(key: string): void {
  if (key !== detailPieceKey) {
    detailPieceKey = key;
    detailMenu.close();
    detailOpen = !drawersCollapsed;
    syncDetailOpen();
  }
  pieceDetailBody.hidden = false;
}

function paintQuestDetailTitle(quest: QuestDetail): void {
  const title = questTitle(quest);
  const href = publishedQuestViewHref(quest);
  pieceDetailTitle.replaceChildren();
  if (!href) {
    pieceDetailTitle.textContent = title;
    return;
  }
  const link = document.createElement("a");
  link.href = href;
  link.textContent = title;
  link.setAttribute("aria-label", t("home.openQuestPage", { title }));
  pieceDetailTitle.append(link);
}

function paintQuestDetail(quest: QuestDetail): void {
  revealDetail(`q:${quest.id}`);
  listPreview.hidePreviewChrome();
  const hasRoute = (quest.route?.coordinates.length ?? 0) >= 2;
  const length = quest.route?.length_m ?? 0;
  const draft = quest.status === "draft";
  paintQuestDetailTitle(quest);
  pieceDetailBadge.hidden = !draft;
  pieceDetailDistance.textContent = hasRoute ? formatDistance(length) : "—";
  pieceDetailPointsLabel.textContent = t("home.estTime");
  pieceDetailPoints.textContent = hasRoute ? walkLabel(length) : "—";
  pieceDetailStrip.hidden = !hasRoute;
  pieceDetailStrip.textContent = questSettingsSummary(quest);
  const when = new Date(quest.created_at).toLocaleDateString(locale());
  const by = quest.author ? t("rt.page.by", { name: quest.author }) : t("quest.byUnknown");
  pieceDetailMeta.textContent = `${by} · ${when}`;
  pieceDetailStatus.hidden = true;
  pieceDetailPublish.hidden = true;
  pieceDetailEdit.hidden = true;
  const own = quest.mine === true && me != null;
  const admin = me?.admin === true;
  const published = !draft;
  questDetailEdit.hidden = !((own && draft) || (own && published && !admin) || (admin && published));
  if (admin && published) {
    questDetailEdit.href = questEditPath(quest.id);
    questDetailEdit.textContent = t("mode.edit");
  } else if (own) {
    if (draft) {
      questDetailEdit.href = questEditPath(quest.id);
      questDetailEdit.textContent = t("mode.edit");
    } else {
      questDetailEdit.href = questViewPath(quest.id);
      questDetailEdit.textContent = t("sheet.view");
    }
  } else {
    questDetailEdit.removeAttribute("href");
  }
  publicPieceDelete.hidden = !templateDeletable(quest, me?.id ?? null);
  paintDetailSaveHeart(quest);
  try {
    syncInlinePage(quest);
  } catch (err) {
    reportError(err, { location: "paintQuestDetail:inline" });
    syncInlinePage(null);
  }
  // Exports read the public endpoint, which a draft never goes through.
  if (draft || !hasRoute) {
    geojsonLink.removeAttribute("href");
    gpxLink.removeAttribute("href");
    geojsonLink.hidden = true;
    gpxLink.hidden = true;
    return;
  }

  const geo = {
    type: "Feature",
    properties: { id: quest.id, title: quest.title, author: quest.author, closed: quest.route?.closed ?? false },
    geometry: { type: "LineString", coordinates: quest.route?.coordinates ?? [] },
  };
  if (geojsonUrl) URL.revokeObjectURL(geojsonUrl);
  geojsonUrl = URL.createObjectURL(new Blob([JSON.stringify(geo, null, 2)], { type: "application/geo+json" }));
  geojsonLink.href = geojsonUrl;
  geojsonLink.download = `${quest.id}.geojson`;
  gpxLink.href = questGpxUrl(quest.id);
  gpxLink.download = `${quest.id}.gpx`;
  geojsonLink.hidden = false;
  gpxLink.hidden = false;
}

function readFillPalette(): FillPaletteId {
  try {
    const stored = localStorage.getItem(FILL_PALETTE_KEY);
    if (stored && isFillPaletteId(stored)) return stored;
  } catch {
    /* private mode */
  }
  return "default";
}

function readFillColor(): string {
  try {
    const stored = localStorage.getItem(FILL_COLOR_KEY);
    if (stored === NO_FILL) return NO_FILL;
    const hex = stored ? normalizeColor(stored) : null;
    if (hex) return hex;
  } catch {
    /* private mode */
  }
  return DEFAULT_FILL_COLOR;
}

function readCustomSlots(): (string | null)[] {
  try {
    const raw = localStorage.getItem(FILL_CUSTOMS_KEY);
    const slots = parseCustomSlots(raw);
    const packed = JSON.stringify(slots);
    if (raw != null && raw !== packed) localStorage.setItem(FILL_CUSTOMS_KEY, packed);
    return slots;
  } catch {
    return parseCustomSlots(null);
  }
}

function writeCustomSlots(): void {
  try {
    localStorage.setItem(FILL_CUSTOMS_KEY, JSON.stringify(customSlots));
  } catch {
    /* private mode */
  }
}

/** Flat index: 0 is No fill, then the set, then the shared custom slots. */
function indexForColor(): number {
  return paletteSelectionIndex(fillColor, fillPaletteId, customSlots);
}

function paletteLabel(id: FillPaletteId): string {
  if (id === "pastel") return t("fillRegion.palette.pastel");
  if (id === "vivid") return t("fillRegion.palette.vivid");
  if (id === "earth") return t("fillRegion.palette.earth");
  if (id === "mono") return t("fillRegion.palette.mono");
  return t("fillRegion.palette.default");
}

interface PaletteSink {
  sets: HTMLElement;
  none: HTMLElement | null;
  swatches: HTMLElement;
  customs: HTMLElement;
  /** The floating F palette tracks the brush color and the arrow-key index. */
  track: boolean;
  includeNone?: boolean;
  /** Highlight this color (line pickers and saved custom strokes). */
  selectionColor?: string | null;
  /** Append the selection when it is not already in the grid. */
  ensureColor?: string | null;
  customPickTarget?: CustomPickTarget;
  onChoose: (color: string) => void;
}

function refreshPalettes(): void {
  paletteGeneration += 1;
  paintFillPalette();
  paintStyleFacePalette();
  paintStyleLinePalette();
  paintDefaultLinePalette();
}

function paintFillPalette(): void {
  fillPalette.setAttribute("aria-label", t("keymap.fillTool"));
  paintPaletteInto({
    sets: fillPaletteSets,
    none: fillPaletteNone,
    swatches: fillPaletteSwatches,
    customs: fillPaletteCustoms,
    track: true,
    selectionColor: fillColor,
    onChoose: chooseFillColor,
  });
}

function paintStyleFacePalette(): void {
  if (stylePanel.hidden) return;
  if (styleFaceGeneration === paletteGeneration) return;
  styleFaceGeneration = paletteGeneration;
  paintPaletteInto({
    sets: styleFaceSets,
    none: styleFaceNone,
    swatches: styleFaceSwatches,
    customs: styleFaceCustoms,
    track: false,
    onChoose: (color) => editor.fillEveryFace(color),
  });
}

function paintStyleLinePalette(): void {
  if (stylePanel.hidden || styleLines.hidden) return;
  if (styleLineGeneration === paletteGeneration) return;
  styleLineGeneration = paletteGeneration;
  paintPaletteInto({
    sets: styleLineSets,
    none: null,
    swatches: styleLineSwatches,
    customs: styleLineCustoms,
    track: false,
    includeNone: false,
    selectionColor: lineStyleColor,
    ensureColor: lineStyleColor,
    customPickTarget: "line",
    onChoose: (color) => {
      const hex = normalizeColor(color);
      if (!hex) return;
      lineStyleColor = hex;
      paintStyleLinePaletteForce();
    },
  });
}

function paintStyleLinePaletteForce(): void {
  styleLineGeneration = -1;
  paintStyleLinePalette();
}

function paintDefaultLinePalette(): void {
  if (styleDefaultsPanel.hidden) return;
  if (defaultLineGeneration === paletteGeneration) return;
  defaultLineGeneration = paletteGeneration;
  paintPaletteInto({
    sets: defaultLineSets,
    none: null,
    swatches: defaultLineSwatches,
    customs: defaultLineCustoms,
    track: false,
    includeNone: false,
    selectionColor: defaultLineStyleColor,
    ensureColor: defaultLineStyleColor,
    customPickTarget: "defaultLine",
    onChoose: (color) => {
      const hex = normalizeColor(color);
      if (!hex) return;
      defaultLineStyleColor = hex;
      saveStyleDefaultsFromForm();
    },
  });
}

function paintPaletteInto(sink: PaletteSink): void {
  const includeNone = sink.includeNone !== false;
  const cells = paletteCells(fillPaletteId, customSlots, {
    includeNone,
    ensureColor: sink.ensureColor ?? null,
  });
  if (sink.track) fillSwatchIndex = Math.max(0, Math.min(cells.length - 1, fillSwatchIndex));
  sink.sets.replaceChildren(
    ...FILL_PALETTE_IDS.map((id) => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = paletteLabel(id);
      button.setAttribute("aria-pressed", String(id === fillPaletteId));
      button.addEventListener("click", () => {
        fillPaletteId = id;
        fillSwatchIndex = indexForColor();
        try {
          localStorage.setItem(FILL_PALETTE_KEY, fillPaletteId);
        } catch {
          /* private mode */
        }
        refreshPalettes();
      });
      return button;
    }),
  );
  const swatchOffset = includeNone ? 1 : 0;
  const customBase = swatchOffset + sharedPaletteSwatches(fillPaletteId).length;
  if (sink.none) {
    const none = cells[0];
    sink.none.replaceChildren(none ? fillCellButton(none, 0, sink, cells) : document.createElement("span"));
  }
  sink.swatches.replaceChildren(
    ...cells
      .filter((cell) => cell.kind === "swatch")
      .map((cell, offset) => fillCellButton(cell, swatchOffset + offset, sink, cells)),
  );
  sink.customs.replaceChildren(
    ...cells
      .filter((cell) => cell.kind === "custom")
      .map((cell, offset) => fillCellButton(cell, customBase + offset, sink, cells)),
  );
  if (!sink.track) return;
  const active = fillPalette.querySelector(`[data-fill-index="${fillSwatchIndex}"]`);
  if (active instanceof HTMLElement) active.scrollIntoView({ block: "nearest" });
}

function fillCellButton(cell: PaletteCell, index: number, sink: PaletteSink, _cells: readonly PaletteCell[]): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "fill-swatch";
  if (sink.track) button.dataset.fillIndex = String(index);
  const selection = sink.selectionColor ?? (sink.track ? fillColor : null);
  const current =
    sink.track && (index === fillSwatchIndex || cellMatchesSelection(cell, selection))
      ? true
      : !sink.track && selection != null && cellMatchesSelection(cell, selection);
  button.setAttribute("aria-current", String(current));
  if (cell.kind === "none") {
    button.classList.add("fill-swatch-none");
    button.setAttribute("aria-label", t("fillRegion.none"));
    button.addEventListener("click", () => {
      if (sink.track) fillSwatchIndex = index;
      sink.onChoose(NO_FILL);
    });
    return button;
  }
  if (cell.kind === "swatch") {
    button.style.background = cell.color;
    button.setAttribute("aria-label", cell.color);
    button.addEventListener("click", () => {
      if (sink.track) fillSwatchIndex = index;
      sink.onChoose(cell.color);
    });
    return button;
  }
  if (cell.color) {
    button.style.background = cell.color;
    button.setAttribute("aria-label", t("fillRegion.customSlot", { n: cell.slot + 1 }));
  } else {
    button.classList.add("fill-swatch-empty");
    button.setAttribute("aria-label", t("fillRegion.customEmpty"));
  }
  button.addEventListener("click", (event) => {
    if (sink.track) fillSwatchIndex = index;
    if (event.altKey) {
      event.preventDefault();
      reassignCustomSlot(cell.slot, cell.color, pickTargetForSink(sink));
      return;
    }
    if (cell.color) sink.onChoose(cell.color);
    else openCustomPicker(cell.slot, pickTargetForSink(sink));
  });
  button.addEventListener("contextmenu", (event) => {
    event.preventDefault();
    if (sink.track) fillSwatchIndex = index;
    reassignCustomSlot(cell.slot, cell.color, pickTargetForSink(sink));
  });
  return button;
}

function pickTargetForSink(sink: PaletteSink): CustomPickTarget {
  if (sink.customPickTarget) return sink.customPickTarget;
  return sink.track ? "brush" : "faces";
}

function cellMatchesSelection(cell: PaletteCell, color: string | null): boolean {
  if (color == null) return false;
  if (color === NO_FILL) return cell.kind === "none";
  const hex = normalizeColor(color) ?? color.toLowerCase();
  if (cell.kind === "none") return false;
  if (cell.kind === "swatch") return cell.color === hex;
  return cell.color != null && cell.color === hex;
}

/** Right-click or Alt-click clears a stored color, or opens the picker on an empty slot. */
function reassignCustomSlot(slot: number, color: string | null, target: CustomPickTarget): void {
  if (color) {
    customSlots[slot] = null;
    writeCustomSlots();
    refreshPalettes();
    return;
  }
  openCustomPicker(slot, target);
}

function openCustomPicker(slot: number, target: CustomPickTarget = "brush"): void {
  customPickSlot = slot;
  customPickTarget = target;
  const current = customSlots[slot];
  const fallback =
    customPickTarget === "line"
      ? lineStyleColor
      : customPickTarget === "defaultLine"
        ? defaultLineStyleColor
        : fillColor === NO_FILL
          ? DEFAULT_FILL_COLOR
          : fillColor;
  fillColorInput.value = current ?? fallback;
  const picker = fillColorInput as HTMLInputElement & { showPicker?: () => void };
  if (typeof picker.showPicker === "function") {
    try {
      picker.showPicker();
      return;
    } catch {
      /* a second gesture falls through to click */
    }
  }
  fillColorInput.click();
}

function activateFillCell(): void {
  const cell = paletteCells(fillPaletteId, customSlots)[fillSwatchIndex];
  if (!cell) return;
  if (cell.kind === "none") chooseFillColor(NO_FILL);
  else if (cell.kind === "swatch") chooseFillColor(cell.color);
  else if (cell.color) chooseFillColor(cell.color);
  else openCustomPicker(cell.slot);
}

function positionFillPalette(anchor?: HTMLElement): void {
  fillPalette.style.left = "0px";
  fillPalette.style.top = "0px";
  const box = anchor?.getBoundingClientRect();
  const at = box ? { x: box.left, y: box.bottom + 8 } : editor.pointerClient();
  const rect = fillPalette.getBoundingClientRect();
  const left = Math.max(8, Math.min(at.x, window.innerWidth - rect.width - 8));
  const top = Math.max(8, Math.min(at.y, window.innerHeight - rect.height - 8));
  fillPalette.style.left = `${left}px`;
  fillPalette.style.top = `${top}px`;
}

/** F opens the palette, and F again closes it and leaves Fill Region. */
function toggleFillTool(): void {
  if (fillPaletteOpen || editor.interactionMode() === "fill") {
    closeFillPalette();
    if (editor.interactionMode() === "fill") editor.setFillMode(false);
    updateHint();
    return;
  }
  closeRoadsForTool();
  openFillPalette();
}

function openFillPalette(anchor?: HTMLElement): void {
  closeDeleteMenu();
  fillPaletteOpen = true;
  editor.setFillPaletteOpen(true);
  fillSwatchIndex = indexForColor();
  paintFillPalette();
  fillPalette.hidden = false;
  positionFillPalette(anchor);
}

Object.assign(window, {
  __mapusOpenFillPalette: () => openFillPalette(),
});

function closeFillPalette(): void {
  if (!fillPaletteOpen) return;
  fillPaletteOpen = false;
  fillPalette.hidden = true;
  editor.setFillPaletteOpen(false);
}

function chooseFillColor(color: string): void {
  if (color !== NO_FILL) {
    const hex = normalizeColor(color);
    if (!hex) return;
    color = hex;
  }
  fillColor = color;
  try {
    localStorage.setItem(FILL_PALETTE_KEY, fillPaletteId);
    localStorage.setItem(FILL_COLOR_KEY, color);
  } catch {
    /* private mode */
  }
  editor.setRegionColor(color);
  closeFillPalette();
  editor.setBoundaryMode(false);
  editor.setEraseMode(false);
  editor.setWorkMode(false);
  editor.setFillMode(true);
  updateHint();
}

function flashActionHint(key: "hint.joinFew" | "hint.mergeFew"): void {
  actionHintUntil = Date.now() + 2800;
  actionHint = t(key);
  updateHint();
  window.setTimeout(() => updateHint(), 2900);
}

const fillHintChip = document.createElement("button");
fillHintChip.type = "button";
fillHintChip.className = "fill-hint-chip";
fillHintChip.addEventListener("click", () => openFillPalette(fillHintChip));

function updateHint(): void {
  hintEl.classList.remove("hint-fill");
  if (session.mode === "view") {
    hintEl.hidden = true;
    return;
  }
  if (actionHintUntil > Date.now()) {
    hintEl.hidden = false;
    hintEl.textContent = actionHint;
    return;
  }
  if (deleteHintUntil > Date.now() && !editor.hasSelection()) {
    hintEl.hidden = false;
    hintEl.textContent = t("delete.none");
    return;
  }
  if (editor.isGrabbing()) {
    hintEl.hidden = false;
    hintEl.textContent = t("move.hint", { count: editor.grabCount() });
    return;
  }
  if (selectToolHintUntil > Date.now()) {
    hintEl.hidden = false;
    if (roadsOpen) hintEl.textContent = t(roadsToolHintKey(editor.roadsToolKind()));
    else hintEl.textContent = t(selectToolHintKey(editor.selectTool()));
    return;
  }
  const mode = editor.interactionMode();
  if (roadsOpen && (mode === "roadsBox" || editor.roadsToolKind() === "box")) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.roadsBox");
    return;
  }
  if (roadsOpen && (mode === "roadsCircle" || editor.roadsToolKind() === "circle")) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.roadsCircle");
    return;
  }
  if (mode === "fill") {
    hintEl.hidden = false;
    hintEl.classList.add("hint-fill");
    fillHintChip.className = fillColor === NO_FILL ? "fill-hint-chip fill-hint-chip-none" : "fill-hint-chip";
    fillHintChip.style.background = fillColor === NO_FILL ? "" : fillColor;
    fillHintChip.setAttribute("aria-label", t("fillRegion.changeColor"));
    const label = document.createElement("span");
    label.className = "fill-hint-text";
    label.textContent = t("hint.fillRegion");
    hintEl.replaceChildren(fillHintChip, label);
    return;
  }
  if (mode === "boundary" && !editor.boundaryIsClosed()) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.boundaryOpen");
    return;
  }
  if (mode === "boundary") {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.boundaryClosed");
    return;
  }
  if (mode === "erase") {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.erase");
    return;
  }
  if (mode === "work") {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.workArea");
    return;
  }
  if (mode === "graph" && editor.selectTool() !== "off") {
    hintEl.hidden = false;
    hintEl.textContent = t(selectToolHintKey(editor.selectTool()));
    return;
  }
  if (editor.pointCount() > 0 && editor.partCount() > 1) {
    hintEl.hidden = false;
    hintEl.textContent = t("route.disconnected");
    return;
  }
  if (mode === "graph" && editor.hasSelection()) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.selected", { count: formatCount(editor.selectionCount()) });
    return;
  }
  if (!editor.canEditAtZoom()) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.zoomIn");
    return;
  }
  const n = editor.pointCount();
  if (editor.hasOffRoad() && n >= 2) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.zoomRedraw");
    return;
  }
  const route = routeForChrome();
  if (route != null && !route.ok && n > 0) {
    hintEl.hidden = false;
    hintEl.textContent = t(route.reason === "disconnected" ? "route.disconnected" : "route.empty");
    return;
  }
  if (n === 0 && !loadedQuest) {
    hintEl.hidden = false;
    hintEl.textContent = t("hint.tap");
    return;
  }
  hintEl.hidden = true;
}

function syncIssueChips(): void {
  const counts = editor.issueCounts();
  const byKind = { gap: counts?.gaps ?? 0, overlap: counts?.overlaps ?? 0, crossing: counts?.crossings ?? 0 };
  const keys = { gap: "issues.gaps", overlap: "issues.overlaps", crossing: "issues.crossings" } as const;
  let any = false;
  for (const [btn, kind] of issueButtons) {
    const count = byKind[kind];
    btn.hidden = count === 0;
    if (count === 0) {
      issueCursor[kind] = 0;
      continue;
    }
    any = true;
    btn.textContent = t(keys[kind], { count });
    btn.title = t("issues.next");
  }
  statsIssuesEl.hidden = !any;
}

function routeForChrome(): RouteResult | null {
  if (editor.routeIsFresh()) return editor.getRoute();
  if (editor.oddVertexCount() > ROUTE_SYNC_ODDS || editor.pointCount() >= ROUTE_SYNC_POINTS) {
    scheduleRouteRefresh();
    return editor.cachedRoute();
  }
  return editor.getRoute();
}

function scheduleRouteRefresh(): void {
  if (routeIdle != null || routeTimer != null) return;
  const run = () => {
    routeIdle = null;
    routeTimer = null;
    if (!editor.routeIsFresh()) editor.getRoute();
    syncChrome(false);
  };
  if (typeof requestIdleCallback === "function") {
    routeIdle = requestIdleCallback(run, { timeout: 48 });
  } else {
    routeTimer = setTimeout(run, 16);
  }
}

function markDirty(): void {
  if (!editingActive(session) && !session.leavePrompt) return;
  session = markSessionDirty(session);
  applySessionChrome();
  draftDirty = true;
  scheduleDraftSave();
}

function scheduleDraftSave(): void {
  if (draftIdle != null || draftTimer != null) return;
  const generation = draftGeneration;
  const run = () => {
    draftIdle = null;
    draftTimer = null;
    if (generation !== draftGeneration || !draftDirty) return;
    draftDirty = false;
    saveDraft({
      title: titleInput.value,
      graph: editor.getGraph(),
    });
  };
  if (typeof requestIdleCallback === "function") {
    draftIdle = requestIdleCallback(run, { timeout: 500 });
  } else {
    draftTimer = setTimeout(run, 200);
  }
}

function cancelDraftSave(): void {
  draftGeneration += 1;
  draftDirty = false;
  if (draftIdle != null) {
    cancelIdleCallback(draftIdle);
    draftIdle = null;
  }
  if (draftTimer != null) {
    clearTimeout(draftTimer);
    draftTimer = null;
  }
}

function flushDraftSave(): void {
  if (!draftDirty) return;
  draftDirty = false;
  if (draftIdle != null) {
    cancelIdleCallback(draftIdle);
    draftIdle = null;
  }
  if (draftTimer != null) {
    clearTimeout(draftTimer);
    draftTimer = null;
  }
  saveDraft({
    title: titleInput.value,
    graph: editor.getGraph(),
  });
}

function stripId(): void {
  const url = new URL(location.href);
  if (!url.searchParams.has("id") && !url.searchParams.has("piece")) return;
  url.searchParams.delete("id");
  url.searchParams.delete("piece");
  history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function rememberSaved(title: string, graph: PieceGraph): void {
  lastSavedTitle = title;
  savedGraph = structuredClone(graph);
}

function applySession(): void {
  applySessionChrome();
  editor.setBrowseMode(session.mode === "view");
  savePrompt.hidden = !session.leavePrompt;
  signinPrompt.hidden = !signinPromptOpen;
  updateHint();
  syncChrome(false);
}

async function deleteLoadedQuest(): Promise<void> {
  const quest = loadedQuest;
  if (!me || !quest || !templateDeletable(quest, me.id)) return;
  if (!window.confirm(t("quest.deleteConfirm"))) return;
  try {
    await deleteMission(quest.id);
  } catch (err) {
    showToast(shownError(err, "quest.deleteFailed"));
    return;
  }
  closeLoadedQuest();
  showToast(t("home.deleted"));
  void refreshNearby();
  void refreshQuestList();
}

function closeLoadedQuest(): void {
  loadedQuest = null;
  editor.setQuestView(null, 0);
  listPreviewQuest = null;
  listPreview.hidePreviewChrome();
  titleInput.value = "";
  editor.clear();
  editor.resetPieceStyleDefaults();
  rememberSaved("", editor.getGraph());
  stripId();
  session = openPieceSession();
  applySession();
  syncNearbyList();
  syncSavedList();
  syncHomeListsCurrent();
}

function applySessionChrome(): void {
  syncSheetOpen();
  appEl.dataset.mode = session.mode;
  paintCommandLabels();
}

/** Own unpublished Piece (or a new Piece id) may enter Edit. Public templates are locked. */
function canEnterEdit(): boolean {
  return ownPieceEditable(activePieceAccess());
}

function activePieceAccess(): ActivePieceAccess {
  return {
    userId: me?.id ?? null,
    hasOwnPiece: activeUserPieceId != null,
    templateOpen: loadedQuest != null || listPreviewQuest != null,
    published: activeUserPiecePublished,
    onServer: activeUserPieceOnServer,
    ownerId: activeUserPieceOwner,
  };
}

/** Label with the live key chord, e.g. "Erase (E)". Remapping in Settings updates on next paint. */
function labelWithShortcut(action: ActionId, label: string): string {
  const binding = storedKeys.keymap[action].find((chord) => chord.type === "key");
  if (!binding) return label;
  return `${label} (${formatBinding(binding)})`;
}

function paintCommandLabels(): void {
  const staticLabels: Array<[HTMLButtonElement, MessageKey]> = [
    [undoBtn, "toolbar.undo"],
    [redoBtn, "toolbar.redo"],
    [roadsBtn, "toolbar.fillRoads"],
    [fillColorBtn, "toolbar.fillColor"],
    [eraseBtn, "toolbar.erase"],
    [boundaryClearBtn, "toolbar.clearArea"],
    [fillBtn, "toolbar.runFillRoads"],
    [moveBtn, "keymap.moveSelected"],
    [joinBtn, "keymap.join"],
    [mergeBtn, "keymap.merge"],
    [deleteMenuBtn, "keymap.deleteMenu"],
    [deleteSelectedBtn, "keymap.deleteSelected"],
    [selectAllBtn, "keymap.selectAll"],
    [deselectBtn, "toolbar.deselect"],
    [deselectAllBtn, "keymap.deselectAll"],
    [copyStyleBtn, "keymap.copyStyle"],
    [pasteStyleBtn, "keymap.pasteStyle"],
    [toggleDecorativeBtn, "keymap.toggleDecorative"],
    [magnetBtn, "toolbar.snap"],
    [workAreaBtn, "toolbar.workArea"],
    [workAreaClearBtn, "toolbar.clearWorkArea"],
  ];
  for (const [btn, key] of staticLabels) {
    const action = btn.dataset.shortcutAction;
    if (!action || !isActionId(action)) continue;
    btn.textContent = labelWithShortcut(action, t(key));
  }
  const pointsOn = editor.pointsVisible();
  pointsToggleBtn.textContent = labelWithShortcut(
    "togglePoints",
    pointsOn ? t("toolbar.hidePoints") : t("toolbar.showPoints"),
  );
  if (!saveBtn.disabled) {
    saveBtn.textContent = labelWithShortcut("savePiece", t("action.save"));
  }
  if (session.mode === "edit") {
    exitBtn.textContent = labelWithShortcut("exitEdit", t("mode.exit"));
  }
  if (!pieceDetailEdit.hidden) {
    pieceDetailEdit.textContent = labelWithShortcut("enterEdit", t("mode.edit"));
  }
  moreBtn.title = labelWithShortcut("toggleDrawers", t("toolbar.more"));
  detailToggleBtn.title = labelWithShortcut("toggleDrawers", t("toolbar.details"));
}

function enterEditMode(): void {
  if (!canEnterEdit()) {
    if (loadedQuest != null || activeUserPiecePublished) showToast(t("toast.locked"));
    return;
  }
  persistHomeBrowseReturn({ returnToHome: true });
  selectedQuestBeforeEdit = selectedQuestId();
  session = enterEdit(session);
  applySession();
}

/** Publish = quest: a Piece is never published directly any more. */
async function onPublish(): Promise<void> {
  showToast(t("toast.publishMoved"));
}

async function leaveEdit(_to: "view"): Promise<void> {
  pendingLeave = () => {
    session = openPieceSession();
    applySession();
  };
  session = requestLeaveEdit(session);
  if (!session.leavePrompt) {
    const next = pendingLeave;
    pendingLeave = null;
    next();
    return;
  }
  applySession();
}

async function leaveThen(next: () => void | Promise<void>): Promise<void> {
  pendingLeave = () => {
    void next();
  };
  if (session.mode === "edit") {
    session = requestLeaveEdit(session);
    if (session.leavePrompt) {
      applySession();
      return;
    }
  }
  const run = pendingLeave;
  pendingLeave = null;
  await run();
}

async function resolveLeave(choice: LeaveChoice): Promise<void> {
  if (choice === "cancel") {
    session = resolveLeaveEdit(session, "cancel");
    pendingLeave = null;
    applySession();
    return;
  }
  if (choice === "save") {
    const ok = await onSave({ stayInEdit: false });
    if (!ok) return;
    const run = pendingLeave;
    pendingLeave = null;
    session = openPieceSession();
    applySession();
    if (run) await run();
    return;
  }
  restoreSavedCopy();
  session = resolveLeaveEdit(session, "discard");
  const run = pendingLeave;
  pendingLeave = null;
  applySession();
  if (run) await run();
}

function restoreSavedCopy(): void {
  titleInput.value = lastSavedTitle;
  if (savedGraph) editor.loadGraph(savedGraph);
  else {
    editor.clear();
    editor.resetPieceStyleDefaults();
  }
  cancelDraftSave();
}

function leaveStayInEdit(): boolean {
  return !session.leavePrompt;
}

async function openUserPiece(id: string): Promise<void> {
  hintEl.hidden = false;
  hintEl.textContent = t("hint.loading");
  try {
    // Ownership depends on who is signed in, so wait for the session check.
    await authReady.catch(() => undefined);
    let piece = await pieceStore.getUserPiece(id);
    let published = false;
    if (me) {
      const ownerId = me.id;
      try {
        const remote = await getUserPiece(id);
        published = remote.published === true;
        piece = {
          id: remote.id,
          name: remote.name,
          data: remote.data,
          updatedAt: remote.updated_at,
          savedToServer: true,
          ownerId,
        };
        await pieceStore.putUserPiece(piece);
      } catch (err) {
        if (!piece) throw err;
      }
    }
    // Another account's (or a signed-out user's) cached copy is not opened.
    if (!piece || !belongsToSignedInUser({ onServer: piece.savedToServer, ownerId: piece.ownerId ?? null }, me?.id ?? null)) {
      throw new Error(t("toast.loadFailed"));
    }
    loadedQuest = null;
    editor.setQuestView(null, 0);
    activeUserPieceId = piece.id;
    activeUserPiecePublished = published;
    activeUserPieceOnServer = piece.savedToServer;
    activeUserPieceOwner = piece.ownerId ?? null;
    titleInput.value = piece.name;
    editor.loadGraph(piece.data.graph);
    rememberSaved(piece.name, piece.data.graph);
    session = openPieceSession();
    applySession();
    history.replaceState(null, "", pieceUrl(piece.id));
    showToast(t("toast.loaded", { title: piece.name }));
  } catch (err) {
    showToast(shownError(err, "toast.loadFailed"));
    session = openPieceSession();
    applySession();
  }
}

/** A signed-out draft from before saved pieces moves into the local store. */
async function migrateDraft(): Promise<void> {
  const draft = loadDraft();
  if (draft && (draft.graph.points.length > 0 || draft.graph.workArea || draft.title.trim())) {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await pieceStore.putUserPiece({
      id,
      name: draft.title.trim() || t("saved.new"),
      data: { graph: draft.graph },
      updatedAt: now,
      savedToServer: false,
    });
    clearDraft();
  }
}

async function deleteOwnPiece(id: string): Promise<void> {
  if (!window.confirm(t("pieces.deleteConfirm"))) return;
  if (me) {
    try {
      await deleteUserPieceRemote(id);
    } catch (err) {
      const local = await pieceStore.getUserPiece(id);
      if (!local?.savedToServer) {
        await pieceStore.deleteUserPiece(id);
      } else {
        showToast(shownError(err, "toast.saveFailed"));
        return;
      }
    }
  }
  await pieceStore.deleteUserPiece(id);
  if (activeUserPieceId === id) {
    activeUserPieceId = null;
    activeUserPiecePublished = false;
    activeUserPieceOnServer = false;
    activeUserPieceOwner = null;
    titleInput.value = "";
    editor.clear();
    editor.resetPieceStyleDefaults();
    rememberSaved("", editor.getGraph());
    session = openPieceSession();
    stripId();
    applySession();
  }
}

function syncSheetOpen(): void {
  const open = sheetOpen;
  sheetEl.classList.toggle("is-open", open);
  moreBtn.setAttribute("aria-expanded", String(open));
}

function syncDetailOpen(): void {
  pieceDetail.classList.toggle("is-open", detailOpen);
  detailToggleBtn.setAttribute("aria-expanded", String(detailOpen));
  if (detailOpen && inlinePage) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => inlinePage?.view.relayout());
    });
  }
  syncMapControlChrome();
}

async function toggleDetailQuestSave(): Promise<void> {
  const id = loadedQuest?.id ?? listPreviewQuest?.id;
  if (!id || detailSaveBusy) return;
  if (!me) {
    promptSignInToSave();
    return;
  }
  const status = loadedQuest?.status ?? listPreviewQuest?.status ?? "active";
  if (!questSaveHeartEnabled(status)) return;
  let saved = loadedQuest?.saved ?? listPreviewQuest?.saved;
  if (typeof saved !== "boolean") {
    try {
      const detail = await getQuest(id);
      saved = detail.saved === true;
      patchQuestSavedInLists(id, saved);
      if (loadedQuest?.id === id) loadedQuest = { ...loadedQuest, saved };
      if (listPreviewQuest?.id === id) paintDetailSaveHeart(listPreviewQuest);
    } catch {
      showToast(t("api.failed", { status: 0 }));
      return;
    }
  }
  const next = !saved;
  detailSaveBusy = true;
  if (loadedQuest?.id === id) paintDetailSaveHeart(loadedQuest);
  else if (listPreviewQuest?.id === id) paintDetailSaveHeart(listPreviewQuest);
  try {
    if (next) await saveQuest(id);
    else await unsaveQuest(id);
    patchQuestSavedInLists(id, next);
    if (loadedQuest?.id === id) {
      loadedQuest = { ...loadedQuest, saved: next };
      inlinePage?.view.setQuestSaved(next);
    }
    void refreshSavedList();
  } catch {
    showToast(t("api.failed", { status: 0 }));
  } finally {
    detailSaveBusy = false;
    if (loadedQuest?.id === id) paintDetailSaveHeart(loadedQuest);
    else if (listPreviewQuest?.id === id) paintDetailSaveHeart(listPreviewQuest);
  }
}

function homeListAdPreview(): boolean {
  return readAdPreviewEnabled() && !editingActive(session);
}

function syncSavedList(): void {
  renderSavedList(savedList, savedQuests, selectedQuestId(), me != null, homeListAdPreview(), nearbyViewer.at());
}

async function refreshSavedList(): Promise<void> {
  if (!me) {
    savedQuests = [];
    syncSavedList();
    return;
  }
  try {
    const viewer = nearbyViewer.at();
    const res = await listSavedQuests(viewer);
    savedQuests = res.quests.filter(homeListed);
    syncSavedList();
  } catch {
    savedList.innerHTML = `<p class="empty">${escapeHtml(t("quests.interestFailed"))}</p>`;
  }
}

/** Mark the selected card in Nearby, My quests, and Saved without re-rendering them. */
function syncHomeListsCurrent(): void {
  const selected = selectedQuestId();
  syncListCurrent(nearbyList, "button.nearby-item[data-id]", (el) => el.dataset.id, selected);
  syncListCurrent(questListItems, "button.piece-item[data-quest-id]", (el) => el.dataset.questId, selected);
  syncListCurrent(savedList, "button.nearby-item[data-id]", (el) => el.dataset.id, selected);
}

function syncNearbyList(): void {
  renderNearbyList(nearbyList, nearby, selectedQuestId(), me != null, homeListAdPreview(), nearbyViewer.at());
}

function selectToolHintKey(tool: ReturnType<MapEditor["selectTool"]>): "hint.selectBox" | "hint.selectCircle" | "hint.selectLasso" | "hint.selectOff" {
  if (tool === "box") return "hint.selectBox";
  if (tool === "circle") return "hint.selectCircle";
  if (tool === "lasso") return "hint.selectLasso";
  return "hint.selectOff";
}

function roadsToolHintKey(tool: ReturnType<MapEditor["roadsToolKind"]>): "hint.roadsBox" | "hint.boundaryOpen" | "hint.roadsCircle" {
  if (tool === "box") return "hint.roadsBox";
  if (tool === "circle") return "hint.roadsCircle";
  return "hint.boundaryOpen";
}

function paintRoadsTool(): void {
  const tool = editor.roadsToolKind();
  boundaryBtn.dataset.tool = tool;
  const label = tool === "box" ? "toolbar.selectBox" : tool === "circle" ? "toolbar.selectCircle" : "toolbar.area";
  boundaryBtn.textContent = labelWithShortcut("cycleRoadsTool", t(label));
  boundaryBtn.classList.toggle("is-active", true);
  boundaryBtn.setAttribute("aria-pressed", "true");
}

function paintSelectTool(): void {
  const tool = editor.selectTool();
  selectToolBtn.dataset.tool = tool;
  const label =
    tool === "box"
      ? "toolbar.selectBox"
      : tool === "circle"
        ? "toolbar.selectCircle"
        : tool === "lasso"
          ? "toolbar.selectLasso"
          : "toolbar.selectOff";
  selectToolBtn.textContent = labelWithShortcut("cycleSelectTool", t(label));
  selectToolBtn.classList.toggle("is-active", tool !== "off");
  selectToolBtn.setAttribute("aria-pressed", String(tool !== "off"));
}

function formatCount(count: number): string {
  return new Intl.NumberFormat(locale()).format(count);
}

function formatEta(meters: number): string {
  if (meters < 40) return "—";
  const minutes = Math.max(1, Math.round((meters / 1000) * 6));
  if (minutes < 60) return t("eta.minutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return t("eta.hours", { count: hours });
  return t("eta.hoursMinutes", { hours, minutes: rest });
}

function shownError(err: unknown, fallback: Parameters<typeof t>[0]): string {
  if (err instanceof Error && isMessageKey(err.message)) return t(err.message);
  if (err instanceof Error && err.message && questRouteId) {
    const known = keyForEnglish(err.message);
    if (known) return t(known);
    if (mentionsPiece(err.message)) return t(fallback);
  }
  if (err instanceof Error && err.message) return err.message;
  return t(fallback);
}

function showToast(message: string): void {
  toastEl.textContent = message;
  toastEl.hidden = false;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastEl.hidden = true;
  }, 2800);
}

let switchingLocale = false;
setLocaleSwitchHandler((lang) => void switchLocale(lang));

/** Other language: keep the work (quest autosave, local draft), then reload in it. */
async function switchLocale(lang: Locale): Promise<void> {
  if (switchingLocale) return;
  if (session.mode === "edit" && session.dirty) {
    showToast(t("lang.saveFirst"));
    return;
  }
  switchingLocale = true;
  try {
    if (questPage?.flushPending && !(await questPage.flushPending())) {
      showToast(t("lang.saveFailed"));
      return;
    }
    flushDraftSave();
    storeLocale(lang);
    location.replace(urlWithoutLang(location.href));
  } finally {
    switchingLocale = false;
  }
}

function promptSignInToSave(): void {
  showToast(t("quests.signInToSave"));
  signinPromptOpen = true;
  applySession();
  renderGoogleButton();
}

async function bootQuestPage(route: NonNullable<typeof activeQuestRoute>): Promise<void> {
  document.body.classList.add("quest-mode");
  questRoot.hidden = false;
  appEl.hidden = true;
  questPage?.destroy();
  questPage = null;
  const requireSignIn = () => {
    showToast(t("quests.signInToCreate"));
    signinPromptOpen = true;
    applySession();
    renderGoogleButton();
  };
  try {
    if (route.kind === "new") {
      if (!me) {
        requireSignIn();
        questRoot.innerHTML = `<div class="quest-shell"><p class="quest-msg">${escapeHtml(t("rt.signIn"))}</p><a class="btn" href="/">${t("quest.backHome")}</a></div>`;
        appendLanguageSwitch(questRoot);
        mountLegalFooter(questRoot.querySelector(".quest-shell") ?? questRoot);
        return;
      }
      const center = editor.map.getCenter();
      mountPicker({
        root: questRoot,
        center: { lat: center.lat, lng: center.lng },
        onToast: showToast,
        onCreated: (quest) => {
          history.pushState(null, "", questEditPath(quest.id));
          void bootQuestPage({ kind: "edit", id: quest.id });
        },
      });
      return;
    }
    if (route.kind === "edit" && !me) {
      requireSignIn();
      questRoot.innerHTML = `<div class="quest-shell"><p class="quest-msg">${escapeHtml(t("rt.signIn"))}</p><a class="btn" href="/">${t("quest.backHome")}</a></div>`;
      appendLanguageSwitch(questRoot);
      mountLegalFooter(questRoot.querySelector(".quest-shell") ?? questRoot);
      return;
    }
    const quest = await getMission(route.id);
    if (quest.quest_type === "route_trace" || quest.quest_type === "spot_trace") {
      const editorPage = new RouteTraceEditor({
        root: questRoot,
        quest,
        mode: route.kind === "edit" && quest.can_edit ? "edit" : "view",
        onToast: showToast,
        signedIn: () => me != null,
        onRequireSignIn: promptSignInToSave,
        onSavedChange: (saved) => {
          if (loadedQuest?.id === quest.id) {
            loadedQuest = { ...loadedQuest, saved };
            paintDetailSaveHeart(loadedQuest);
            inlinePage?.view.setQuestSaved(saved);
          }
          void refreshSavedList();
        },
      });
      editorPage.mount();
      questPage = editorPage;
      return;
    }
    const legacy = new QuestPage({
      route,
      user: me,
      root: questRoot,
      onRequireSignIn: requireSignIn,
      onToast: showToast,
    });
    questPage = legacy;
    await legacy.mount();
  } catch (err) {
    showToast(shownError(err, "quests.loadFailed"));
    questRoot.innerHTML = `<div class="quest-shell"><p class="quest-msg">${escapeHtml(shownError(err, "quests.loadFailed"))}</p><a class="btn" href="/">${t("quest.backHome")}</a></div>`;
    appendLanguageSwitch(questRoot);
    mountLegalFooter(questRoot.querySelector(".quest-shell") ?? questRoot);
  }
}

window.addEventListener("popstate", () => {
  const route = matchQuestRoute(location.pathname);
  if (route && route.kind !== "route" && !questRouteId) void bootQuestPage(route);
  else location.reload();
});

function pieceUrl(pieceId: string): string {
  if (questRouteId) return questRoutePath(questRouteId);
  return `/?piece=${encodeURIComponent(pieceId)}`;
}

let questRouteOpen = false;

function paintRouteDocumentTitle(): void {
  document.title = questRouteTitle
    ? t("qroute.docTitle", { title: questRouteTitle })
    : t("qroute.docTitleUntitled");
}

/** The route window can't open: say why, in place of the editor. */
function showRouteMessage(message: string, opts: { signIn?: boolean; questId?: string }): void {
  document.body.classList.add("quest-mode");
  appEl.hidden = true;
  questRoot.hidden = false;
  const link = opts.questId
    ? `<a class="btn" href="${questViewPath(opts.questId)}">${escapeHtml(t("qroute.openQuest"))}</a>`
    : `<a class="btn" href="/">${escapeHtml(t("quest.backHome"))}</a>`;
  questRoot.innerHTML = `<div class="quest-shell route-window-msg"><h1>${escapeHtml(t("qroute.title"))}</h1><p class="quest-msg">${escapeHtml(message)}</p>${opts.signIn ? '<div id="route-signin-google" class="route-signin"></div>' : ""}<div class="route-window-msg-actions">${link}</div></div>`;
  appendLanguageSwitch(questRoot);
  mountLegalFooter(questRoot.querySelector(".quest-shell") ?? questRoot);
  if (opts.signIn) renderGoogleButton();
}

/**
 * `/quest/:id/route`: the map editor bound to a quest draft's route. Opens the route
 * already bound to the quest (or a blank one) straight in Edit; Save binds it and
 * tells the quest tab. The author edits drafts; admins may edit published quests live.
 */
async function openQuestRoute(questId: string): Promise<void> {
  hintEl.hidden = false;
  hintEl.textContent = t("qroute.loading");
  await authReady.catch(() => undefined);
  if (!me) {
    showRouteMessage(t("qroute.signIn"), { signIn: true });
    return;
  }
  let pieceId: string | null = null;
  let center: [number, number] | null = null;
  let adminLive = false;
  let routeFromQuest: QuestDetail["route"] | null = null;
  let questRoute: QuestDetail["route"] | null = null;
  let routeContextQuest: QuestDetail | null = null;
  try {
    const quest = await getMission(questId);
    routeContextQuest = quest;
    const access = routeAccess(quest);
    if (access === "notOwner") {
      showRouteMessage(t("qroute.notOwner"), {});
      return;
    }
    if (access === "published") {
      showRouteMessage(t("qroute.locked"), { questId });
      return;
    }
    questRouteTitle = quest.title.trim();
    adminLive = questRouteAdminLive(quest);
    questRouteLiveEdit = adminLive;
    questRoutePublished = quest.status !== "draft";
    const boundPiece =
      quest.mine || quest.can_edit ? (quest.route_piece_id ?? null) : null;
    pieceId = boundPiece ?? readQuestRoutePieceId(questId);
    if (boundPiece) clearQuestRoutePieceId(questId);
    const route = quest.route && quest.route.coordinates.length >= 2 ? quest.route : null;
    questRoute = route;
    routeFromQuest = adminLive && !pieceId ? route : null;
    center = [quest.center_lng, quest.center_lat];
  } catch (err) {
    showRouteMessage(
      err instanceof ApiError && err.status === 404 ? t("qroute.notOwner") : shownError(err, "quests.loadFailed"),
      {},
    );
    return;
  }
  questRouteOpen = true;
  paintRouteDocumentTitle();
  paintQuestRouteBanner({
    questTitle: questRouteTitle,
    published: questRoutePublished,
    appRoot: appEl,
    mapEl,
    onDone: () => void finishQuestRoute(questId),
  });
  if (pieceId) {
    await openUserPiece(pieceId);
    if (questRoute?.canvas && !editor.hasWorkArea()) {
      const graph = graphFromQuestRoute(questRoute.coordinates, questRoute.closed, questRoute.canvas, questRoute.paint);
      editor.loadGraph(graph);
      rememberSaved(titleInput.value, graph);
    }
    if (canEnterEdit()) {
      const spotPins = routeContextQuest ? homeSpotPinsFromDetail(routeContextQuest) : null;
      if (spotPins?.length) editor.setQuestView(null, 0, null, null, spotPins);
      session = enterEdit(session);
      applySession();
      return;
    }
  }
  titleInput.value = questRouteTitle || t("qroute.defaultName");
  loadedQuest = null;
  activeUserPieceId = crypto.randomUUID();
  activeUserPiecePublished = false;
  activeUserPieceOnServer = false;
  activeUserPieceOwner = null;
  if (routeFromQuest) {
    const graph = graphFromQuestRoute(
      routeFromQuest.coordinates,
      routeFromQuest.closed,
      routeFromQuest.canvas,
      routeFromQuest.paint,
    );
    editor.loadGraph(graph);
    rememberSaved(titleInput.value, graph);
    editor.setQuestView(
      routeFromQuest.coordinates,
      routeCanvasBearing(routeFromQuest.canvas),
      routeFromQuest.paint,
      routeFromQuest.fill,
      null,
      routeFromQuest.canvas ?? null,
    );
  } else if (questRoute) {
    const graph = graphFromQuestRoute(questRoute.coordinates, questRoute.closed, questRoute.canvas, questRoute.paint);
    editor.loadGraph(graph);
    rememberSaved(titleInput.value, graph);
    editor.setQuestView(
      questRoute.coordinates,
      routeCanvasBearing(questRoute.canvas),
      questRoute.paint,
      questRoute.fill,
      null,
      questRoute.canvas ?? null,
    );
  } else {
    editor.clear();
    editor.resetPieceStyleDefaults();
    rememberSaved(titleInput.value, editor.getGraph());
    const spotPins = routeContextQuest ? homeSpotPinsFromDetail(routeContextQuest) : null;
    if (spotPins?.length) editor.setQuestView(null, 0, null, null, spotPins);
    else editor.setQuestView(null, 0);
  }
  cancelDraftSave();
  session = enterEdit(createEditorSession());
  applySession();
  if (!editor.hasWorkArea() && editor.pointCount() === 0 && center) {
    editor.map.jumpTo({ center, zoom: MIN_EDIT_ZOOM + 1 });
  }
}

async function sendRouteToQuest(questId: string, pieceId: string): Promise<boolean> {
  try {
    await bindQuestRoute(questId, pieceId);
    announceRouteSaved(questId);
    showToast(t("qroute.sent"));
    return true;
  } catch (err) {
    showToast(shownError(err, "qroute.failed"));
    return false;
  }
}

/** Done: save unsaved changes, then close this tab (or go to the quest when it wasn't opened by one). */
async function finishQuestRoute(questId: string): Promise<void> {
  if (editingActive(session) && session.dirty && !(await onSave({ stayInEdit: true }))) return;
  const stored = readHomeBrowseReturn();
  const returnHome = stored?.returnToHome === true;
  const homeHref = returnHome ? homeReturnHref(questId, stored) : questEditPath(questId);
  if (returnHome && stored) {
    writeHomeBrowseReturn({
      questId: stored.questId ?? questId,
      view: stored.view,
      returnToHome: true,
    });
  }
  if (window.opener && !window.opener.closed) {
    try {
      window.opener.focus();
      if (returnHome) window.opener.location.replace(homeHref);
    } catch {
      /* cross-window navigation blocked */
    }
  }
  window.close();
  window.setTimeout(() => {
    location.replace(homeHref);
  }, 300);
}

/**
 * My quests: your published quests and drafts (blank titles show as Untitled), newest
 * first. Each opens on the map and in the detail panel; drafts link to Edit, published to View.
 */
async function refreshQuestList(): Promise<void> {
  if (!me) {
    myListedQuests = [];
    questListItems.replaceChildren();
    questListEmpty.hidden = false;
    questListEmpty.textContent = t("quests.signInToCreate");
    return;
  }
  try {
    const missions = myQuestsListed((await listMyMissions(nearbyViewer.at())).missions);
    questListEmpty.hidden = missions.length > 0;
    questListEmpty.textContent = t("quests.empty");
    myListedQuests = renderMyQuestList(questListItems, missions, selectedQuestId(), homeListAdPreview());
  } catch {
    myListedQuests = [];
    questListItems.replaceChildren();
    questListEmpty.hidden = false;
    questListEmpty.textContent = t("quests.loadFailed");
  }
}

/** New quest needs an account: signed out, it opens the sign-in prompt instead. */
function guardNewQuest(e: Event): void {
  if (me) return;
  e.preventDefault();
  showToast(t("quests.signInToCreate"));
  signinPromptOpen = true;
  applySession();
  renderGoogleButton();
}

questCreateBtn.addEventListener("click", guardNewQuest);

questDetailSaveHeart.addEventListener("click", () => void toggleDetailQuestSave());
questDetailExpand.addEventListener("click", () => {
  const id = listPreviewQuest?.id;
  if (id) void loadById(id, { fit: false, listOpenGen: ++homeListCardGen });
});
questDetailClose.addEventListener("click", () => clearHomeQuestSelection());
questDetailEdit.addEventListener("click", () => {
  const questId = loadedQuest?.id ?? listPreviewQuest?.id ?? null;
  persistHomeBrowseReturn({
    returnToHome: true,
    questId,
  });
  if (questId) questDetailEdit.href = questEditPath(questId);
});

function must<T extends HTMLElement = HTMLElement>(sel: string): T {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`missing ${sel}`);
  return el;
}

/** A text field keeps every key; a focused slider keeps only its arrow/Home/End keys. */
function isTyping(target: EventTarget | null, code: string): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const type = target instanceof HTMLInputElement ? target.type : undefined;
  return focusOwnsKey({ tagName: target.tagName, type, isContentEditable: target.isContentEditable }, code);
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
